import React, { useContext, useState, useEffect, useRef } from "react";
import Modal from "react-modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import apiService from "../services/apiService";
import { useToast } from "../contexts/ToastContext";
import { useConfirm } from "../contexts/ConfirmContext";
import { useSurveyWebSocket } from "./hooks/useSurveyWebSocket";
import { useAutoSave } from "./hooks/useAutoSave";
import { useUndoRedo } from "./hooks/useUndoRedo";
import { useChangeLog } from "./hooks/useChangeLog";
import ChangeLogModal from "./ChangeLogModal";
import { FormattableInput } from "./FormattableInput";
import { useAuth } from "../providers/AuthProvider";
import {
  faListAlt,
  faEllipsisV,
  faTrashAlt,
  faGripLines,
  faDownload,
  faSave,
  faTable,
  faFileExport,
  faCog,
  faChevronDown,
  faTimes,
  faFileCircleCheck,
  faRotateLeft,
  faRotateRight,
  faClockRotateLeft,
} from "@fortawesome/free-solid-svg-icons";

import "./styles/SurveyBuilder.css";
import VariableContext from "../contexts/VariableContext";
import FetchingFreq from "../contexts/FetchingFreq";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import RowColumnComponent from "./RowColumnComponent";
import HoverDropdown from "./HoverDropdown";
import OESettingsComponent from "./OESettingsComponent";
import SurveyPreview from "./SurveyPreview";
import QuestionPreview from "./QuestionPreview";
import VoiceAssistant from "./VoiceAssistant";
import { Button } from "react-bootstrap";
import CommonButton from "../elments/CommonButton";
import QuotaModal from "./survey-scriping/QuotaModal";
import RedirectModal from "./survey-scriping/RedirectModal";
import AllListsModal from "./survey-scriping/AllListsModal"; // Add this import
import AddToTableModal from "./survey-scriping/AddToTableModal";
import ExportOptionsDropdown from "./survey-scriping/ExportOptionsDropdown";
import { useListProcessor } from "./hooks/useListProcessor";
import BulkActionsModal from "./BulkActionsModal";
import { normalizeRequiredFlags } from "../utils/surveyNormalization";
import { renameQuestionReferences } from "../utils/questionIdRename";

const IMPORT_KEY = "qnr_export_json";

const mapImportedQuestions = (arr = []) => {
  const obj = {};
  arr.forEach((q, idx) => {
    const key = q.Question_number || `Q${idx + 1}`;
    obj[key] = { ...q, _order: q._order || idx + 1 };
  });
  return obj;
};

const SurveyBuilder = () => {
  const {
    dataGroup,
    setdataGroup,
    sdataGroup,
    setSdataGroup,
    selectedVariables,
    banners,
    setBanners,
    setSelectedVariables,
    projectId,
    savedWeightings,
    saveCurrentWeighting,
    currentSelectedVariablesset,
    setcurrentSelectedVariablesset,
    setSavedWeightings,
    weightingStats,
    powner,
    setWeightingStats,
    currentSelectedVariables,
    setCurrentSelectedVariables,
    setSelectedClient,
    setSelectedProject,
    selectedProject,
    selectedClient,
    surveyFileName,
    setSurveyFileName,
    surveyName,
    setSurveyName,
    updateSdataGroup,
    pID,
    pName
  } = useContext(VariableContext);
  const {
    setActivePage,
    handleTitleClick,
    selectedTitle,
    isModalOpen,
    rows = [],
    setRows,
    statements = [],
    setStatements,
    columns = [],
    setColumns,
    setIsModalOpen,
    setSelectedTitle,
    refreshData,
    setRefreshData,
    selectedBanner,
    setselectedBanner,
    selectedWeights,
    setSelectedWeights,
    selectedVariable,
    setSelectedVariable,
    loading,
    setLoading,
    list,
    setList,
    oeParameters,
    setOeParameters,
    setSelectedQuestionKey,
  } = useContext(FetchingFreq);
  const { user } = useAuth();
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const [allowedDownloads, setAllowedDownloads] = useState([]);
  const [activeTab, setActiveTab] = useState("rows");
  const [questions, setQuestions] = useState({});
  const [latestDataGroup, setLatestDataGroup] = useState(sdataGroup);
  const [autoPreQuotes, setAutoPreQuotes] = useState(false);
  const [alphaOrder, setAlphaOrder] = useState(false);
  const [columnIndex, setColumnIndex] = useState(false);
  const [screenOutSelections, setScreenOutSelections] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [matchQuestionNumber, setMatchQuestionNumber] = useState("");
  const [pasteModalOpen, setPasteModalOpen] = useState(false);
  const [pendingPaste, setPendingPaste] = useState(null);
  const [toastMessage, setToastMessage] = useState("");
  const [processingMessage, setProcessingMessage] = useState("");
  const [questionnaireFileId, setQuestionnaireFileId] = useState("");
  const [exportedFiles, setExportedFiles] = useState({});
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [showRedirectModal, setShowRedirectModal] = useState(false);
  const [isAllListsModalOpen, setIsAllListsModalOpen] = useState(false);
  const [toolSelectionModalOpen, setToolSelectionModalOpen] = useState(false);
  const [removeNumber, setRemoveNumber] = useState(false); // New state for tool selection
  const [hideme, setHideme] = useState(false); // New state for tool selection
  const [qtitlehide, setQtitlehide] = useState(false); // New state for tool selection
  const [removebold, setRemovebold] = useState(false); // New state for tool selection
  const [removinstt, setRemovinstt] = useState(false); // New state for tool selection

  const [redirectData, setRedirectData] = useState({ screenout: '', terminate: '', quotafull: '' });

  const [quotaData, setQuotaData] = useState({
    quotas: [],
    nextQuotaNumber: 2,
  });

  const [bulkActionsModalOpen, setBulkActionsModalOpen] = useState(false);
  const [selectedQuestionsForBulk, setSelectedQuestionsForBulk] = useState([]);

  // Change log: when true, the next sdataGroup change is programmatic
  // (remote apply, undo/redo, or revert) and must not be logged locally.
  const suppressChangelogRef = useRef(false);
  // Holds the change-log hook's remote handlers so the websocket callbacks can
  // reach them (breaks the hook ↔ hook dependency cycle).
  const changelogApiRef = useRef({ addRemoteEntry: null, applyRemoteRevert: null });
  const [changeLogModalOpen, setChangeLogModalOpen] = useState(false);
  // null = whole-survey history; a question key = per-question filtered view.
  const [changeLogFilterKey, setChangeLogFilterKey] = useState(null);
  // Captures the Question_number as it was when the field gained focus, so we
  // only cascade a rename once editing is committed (on blur), not on every
  // keystroke — typing "Q1" -> "Q10" shouldn't trigger a rename mid-type.
  const questionNumberFocusValueRef = useRef(null);

  const getSortedQuestionKeys = () =>
    Object.entries(sdataGroup)
      .sort((a, b) => (a[1]._order || 0) - (b[1]._order || 0))
      .map(([k]) => k);

  const moveSelectedQuestions = (direction) => {
    if (selectedQuestionsForBulk.length === 0) return;
    const arr = getSortedQuestionKeys();
    const selectedSet = new Set(selectedQuestionsForBulk);

    if (direction === "up") {
      for (let i = 1; i < arr.length; i++) {
        if (selectedSet.has(arr[i]) && !selectedSet.has(arr[i - 1])) {
          [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
        }
      }
    } else {
      for (let i = arr.length - 2; i >= 0; i--) {
        if (selectedSet.has(arr[i]) && !selectedSet.has(arr[i + 1])) {
          [arr[i], arr[i + 1]] = [arr[i + 1], arr[i]];
        }
      }
    }

    const updated = { ...sdataGroup };
    arr.forEach((key, idx) => {
      updated[key] = { ...updated[key], _order: idx + 1 };
    });
    setSdataGroup(updated);
  };

  const handleBulkDelete = async () => {
    if (selectedQuestionsForBulk.length === 0) return;
    const ok = await confirm({
      title: "Delete Questions",
      message: `Delete ${selectedQuestionsForBulk.length} selected question(s)? This cannot be undone.`,
      confirmText: "Delete",
      variant: "danger",
    });
    if (!ok) return;

    const updated = { ...sdataGroup };
    selectedQuestionsForBulk.forEach((key) => delete updated[key]);
    setSdataGroup(updated);

    if (selectedQuestionsForBulk.includes(selectedTitle)) {
      setSelectedTitle(null);
      setSelectedVariable({ Qtitle: "", instructions: "", rows: [] });
      setRows([]);
      setColumns([]);
      setStatements([]);
    }

    toast.success(`Deleted ${selectedQuestionsForBulk.length} question(s).`);
    setSelectedQuestionsForBulk([]);
  };

  // ========== WEBSOCKET COLLABORATION STATE ==========
  const currentUserEmail = user?.user?.email || user?.email;
  const currentUsername =
    user?.user?.username ||
    user?.username ||
    currentUserEmail?.split("@")[0] ||
    "Anonymous";

  // For avatars/display, you can extract first letter
  const userInitial = currentUsername?.charAt(0)?.toUpperCase() || "U";

  console.log("🔐 Current authenticated user:", {
    email: currentUserEmail,
    username: currentUsername,
    fullUser: user,
  });
  const [notifications, setNotifications] = useState([]);

  // Notification helper
  const showNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 5000);
  };

  const [questionType, setQuestionType] = useState([
    { type: "1", text: "SingleResponse" },
    { type: "2", text: "MultipleResponse" },
    { type: "3", text: "SingleGrid" },
    { type: "4", text: "MultiGrid" },
    { type: "5", text: "Numeric" },
    { type: "6", text: "String" },
    { type: "7", text: "Intro" },
    { type: "8", text: "NumericGrid" },
    { type: "9", text: "Numeric2DGrid" },
    { type: "10", text: "StringGrid" },
    { type: "11", text: "Single2DGrid" },
    { type: "12", text: "SingleGridSlider" },
    { type: "13", text: "CustomRows" },
    { type: "14", text: "loop" },
    { type: "15", text: "loopend" },
    { type: "16", text: "CustomCols" },
  ]);
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [showGlobalSettingsDropdown, setShowGlobalSettingsDropdown] = useState(false);
  const [codeDialogOpen, setCodeDialogOpen] = useState(false);
  const [generatedCode, setGeneratedCode] = useState("");
  const [codeDialogTitle, setCodeDialogTitle] = useState("");
  const [templateOptions, setTemplateOptions] = useState([
    {
      value: "p196111141178.xml",
      label: "Toluna 2025",
      columns: 5215,
      lstcol: 3,
    },
    {
      value: "p353855405643.xml",
      label: "Annalect 2025",
      columns: 1088,
      lstcol: 255,
    },
    {
      value: "p742368592644.xml",
      label: "Taverner online 2025",
      columns: 190,
      lstcol: 163,
    },
    {
      value: "p358286899878.xml",
      label: "Taverner Cati 2025",
      columns: 399,
      lstcol: 371,
    },
    {
      value: "p576814749817.xml",
      label: "Taverner Capi 2025",
      columns: 498,
      lstcol: 97,
    },

    {
      value: "p403592984109.xml",
      label: "Consult IMI test",
      columns: 37,
      lstcol: 32,
    }
  ]);
  const [selectedTemplateValue, setSelectedTemplateValue] =
    useState("p734814077083.xml");

  useEffect(() => {
    setActivePage("survey");
  }, []);

  // ========== WEBSOCKET COLLABORATION HOOK ==========
  const {
    isConnected,
    activeUsers,
    questionLocks,
    connectionError,
    emitPropertyChange,
    emitRowAdd,
    emitRowUpdate,
    emitRowDelete,
    emitBulkRowsAdd,
    emitQuestionDelete,
    emitQuestionCreate,
    emitEditingStart,
    emitEditingEnd,
    requestSync,
    emitChangelogAdd,
    emitChangelogRevert,
  } = useSurveyWebSocket({
    projectId,
    userEmail: currentUserEmail, // Using email as unique identifier
    username: currentUsername,

    // Change log — collaborator's entries / reverts arrive here.
    onChangelogAdd: (entry) => changelogApiRef.current.addRemoteEntry?.(entry),
    onChangelogRevert: (data) =>
      changelogApiRef.current.applyRemoteRevert?.(data),

    onPropertyUpdate: (data) => {
      console.log("📩 Property update from:", data.userEmail, data.username);
      suppressChangelogRef.current = true; // remote apply — don't log locally

      setSdataGroup((prev) => {
        if (!prev[data.questionKey]) return prev;
        return {
          ...prev,
          [data.questionKey]: {
            ...prev[data.questionKey],
            [data.property]: data.value,
          },
        };
      });

      if (selectedTitle === data.questionKey) {
        setSelectedVariable((prev) => ({
          ...prev,
          [data.property]: data.value,
        }));
      }

      showNotification(`${data.username} updated ${data.property}`, "info");
    },

    onRowUpdate: (data) => {
      console.log("📩 Row update from:", data.userEmail);
      suppressChangelogRef.current = true; // remote apply — don't log locally
      const { questionKey, rowIndex, rowType, field, value } = data;

      setSdataGroup((prev) => {
        if (!prev[questionKey]) return prev;
        const question = prev[questionKey];
        const items = question[rowType] || [];

        const updatedItems = items.map((item, idx) => {
          if (item.index === rowIndex || idx === rowIndex) {
            return { ...item, [field]: value };
          }
          return item;
        });

        return {
          ...prev,
          [questionKey]: { ...question, [rowType]: updatedItems },
        };
      });

      if (selectedTitle === questionKey) {
        const setter =
          rowType === "rows"
            ? setRows
            : rowType === "columns"
              ? setColumns
              : setStatements;
        setter((prev) =>
          prev.map((item, idx) =>
            item.index === rowIndex || idx === rowIndex
              ? { ...item, [field]: value }
              : item
          )
        );
      }
    },

    onRowAdd: (data) => {
      console.log("📩 Row added by:", data.userEmail);
      suppressChangelogRef.current = true; // remote apply — don't log locally
      const { questionKey, rowData, rowType, position } = data;

      setSdataGroup((prev) => {
        if (!prev[questionKey]) return prev;
        const question = prev[questionKey];
        const items = [...(question[rowType] || [])];

        if (position !== undefined) {
          items.splice(position, 0, rowData);
        } else {
          items.push(rowData);
        }

        return {
          ...prev,
          [questionKey]: { ...question, [rowType]: items },
        };
      });

      if (selectedTitle === questionKey) {
        const setter =
          rowType === "rows"
            ? setRows
            : rowType === "columns"
              ? setColumns
              : setStatements;
        setter((prev) => {
          const newItems = [...prev];
          if (position !== undefined) {
            newItems.splice(position, 0, rowData);
          } else {
            newItems.push(rowData);
          }
          return newItems;
        });
      }

      showNotification(
        `${data.username} added a ${rowType.slice(0, -1)}`,
        "info"
      );
    },

    onRowDelete: (data) => {
      console.log("📩 Row deleted by:", data.userEmail);
      suppressChangelogRef.current = true; // remote apply — don't log locally
      const { questionKey, rowIndex, rowType } = data;

      setSdataGroup((prev) => {
        if (!prev[questionKey]) return prev;
        const question = prev[questionKey];
        const items = (question[rowType] || []).filter(
          (item, idx) => item.index !== rowIndex && idx !== rowIndex
        );

        return {
          ...prev,
          [questionKey]: { ...question, [rowType]: items },
        };
      });

      if (selectedTitle === questionKey) {
        const setter =
          rowType === "rows"
            ? setRows
            : rowType === "columns"
              ? setColumns
              : setStatements;
        setter((prev) =>
          prev.filter(
            (item, idx) => item.index !== rowIndex && idx !== rowIndex
          )
        );
      }
    },

    onBulkRowsAdd: (data) => {
      console.log("📩 Bulk paste by:", data.userEmail);
      suppressChangelogRef.current = true; // remote apply — don't log locally
      const { questionKey, rowsData, rowType, mode } = data;

      setSdataGroup((prev) => {
        if (!prev[questionKey]) return prev;
        const question = prev[questionKey];
        let items = question[rowType] || [];

        if (mode === "replace") items = rowsData;
        else if (mode === "prepend") items = [...rowsData, ...items];
        else items = [...items, ...rowsData];

        return {
          ...prev,
          [questionKey]: { ...question, [rowType]: items },
        };
      });

      if (selectedTitle === questionKey) {
        const setter =
          rowType === "rows"
            ? setRows
            : rowType === "columns"
              ? setColumns
              : setStatements;
        setter((prev) => {
          if (mode === "replace") return rowsData;
          if (mode === "prepend") return [...rowsData, ...prev];
          return [...prev, ...rowsData];
        });
      }

      showNotification(
        `${data.username} pasted ${rowsData.length} items`,
        "info"
      );
    },

    onQuestionDelete: (data) => {
      console.log("📩 Question deleted by:", data.userEmail);
      suppressChangelogRef.current = true; // remote apply — don't log locally

      setSdataGroup((prev) => {
        const newData = { ...prev };
        delete newData[data.questionKey];
        return newData;
      });

      if (selectedTitle === data.questionKey) {
        setSelectedTitle(null);
        setSelectedVariable({ Qtitle: "", instructions: "", rows: [] });
        setRows([]);
        setColumns([]);
        setStatements([]);
      }

      showNotification(
        `${data.username} deleted ${data.questionKey}`,
        "warning"
      );
    },

    onQuestionCreate: (data) => {
      console.log("📩 Question created by:", data.userEmail);
      suppressChangelogRef.current = true; // remote apply — don't log locally

      setSdataGroup((prev) => ({
        ...prev,
        [data.questionKey]: data.questionData,
      }));

      showNotification(`${data.username} created ${data.questionKey}`, "info");
    },

    onUserJoin: (data) => {
      console.log("👋 User joined:", data.userEmail);
      showNotification(`${data.username} joined`, "info");
    },

    onUserLeave: (data) => {
      console.log("👋 User left:", data.userEmail);
      showNotification(`${data.username} left`, "info");
    },

    onQuestionLock: () => { },
    onQuestionUnlock: () => { },
  });

  // Auto-save with email
  // const { forceSave, lastSaved } = useAutoSave({
  //   projectId,
  //   userEmail: currentUserEmail,
  //   sdataGroup,
  //   enabled: true,
  //   debounceDelay: 5000,
  //   onSaveSuccess: (result) => {
  //     console.log("✅ Auto-saved at", result.timestamp);
  //   },
  //   onSaveError: (error) => {
  //     console.error("❌ Auto-save failed", error);
  //     showNotification("Auto-save failed", "warning");
  //   },
  // });

  // Fetch quota config (config_json) on load, independent of QuotaModal
  useEffect(() => {
    const fetchQuotaConfig = async () => {
      if (!projectId) return;
      try {
        const res = await fetch(`${apiService.baseURL}/get-quota-config/${projectId}`, {
          credentials: 'include',
        });
        const result = await res.json();

        // result.quotaData == config_json => { quotas: [...], redirects: {...}, nextQuotaNumber }
        if (result.success && result.quotaData?.quotas) {
          setQuotaData({
            quotas: result.quotaData.quotas,
            redirects: result.quotaData.redirects || { screenout: "", terminate: "", quotafull: "" },
            nextQuotaNumber: result.quotaData.nextQuotaNumber || result.quotaData.quotas.length + 1,
          });
        }
      } catch (err) {
        console.error("Failed to fetch quota config_json:", err);
      }
    };

    fetchQuotaConfig();
  }, [projectId]);

  useEffect(() => {
    try {
      const rawLocal =
        typeof localStorage !== "undefined"
          ? localStorage.getItem(IMPORT_KEY)
          : null;
      const rawSession =
        typeof sessionStorage !== "undefined"
          ? sessionStorage.getItem(IMPORT_KEY)
          : null;

      let parsed = null;
      if (rawLocal) {
        parsed = JSON.parse(rawLocal);
      } else if (rawSession) {
        parsed = JSON.parse(rawSession);
      } else if (window.__QNR_EXPORT__) {
        parsed = window.__QNR_EXPORT__;
      }

      if (!parsed) return;
      const mapped = mapImportedQuestions(parsed);
      if (!Object.keys(mapped).length) return;

      setSdataGroup(mapped);
      const firstKey = Object.keys(mapped)[0];
      const first = mapped[firstKey];
      setSelectedTitle(firstKey);
      setSelectedVariable(first);
      setRows(first.rows || []);
      setColumns(first.columns || []);
      setStatements(first.statements || []);
      setOeParameters(first.oeparameters || {});
      if (typeof localStorage !== "undefined") {
        localStorage.removeItem(IMPORT_KEY);
      }
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.removeItem(IMPORT_KEY);
      }
      if (window.__QNR_EXPORT__) {
        delete window.__QNR_EXPORT__;
      }
      toast.success(`Imported ${parsed.length} questions from QnrDesign`);
    } catch (e) {
      console.error("Failed to import exported questionnaire", e);
    }
  }, [
    setSdataGroup,
    setSelectedTitle,
    setSelectedVariable,
    setRows,
    setColumns,
    setStatements,
    setOeParameters,
  ]);

  useEffect(() => {
    const importFromQnrDesign = async () => {
      try {
        // Method 1: Check window object first (fastest, for small datasets)
        if (window.__QNR_EXPORT__) {
          console.log("📥 Found export in window object");
          const parsed = window.__QNR_EXPORT__;
          const mapped = mapImportedQuestions(parsed);

          if (Object.keys(mapped).length > 0) {
            setSdataGroup(mapped);
            const firstKey = Object.keys(mapped)[0];
            const first = mapped[firstKey];
            setSelectedTitle(firstKey);
            setSelectedVariable(first);
            setRows(first.rows || []);
            setColumns(first.columns || []);
            setStatements(first.statements || []);
            setOeParameters(first.oeparameters || {});
            delete window.__QNR_EXPORT__;
            toast.success(`Imported ${parsed.length} questions from QnrDesign`);
            return;
          }
        }

        // Method 2: Check if there's an export ID in localStorage
        const exportId = localStorage.getItem("qnr_export_id");
        if (!exportId) {
          console.log("📭 No export ID found");
          return;
        }

        console.log("📥 Fetching export from backend:", exportId);

        // Fetch the data from backend
        const response = await fetch(
          `${apiService.baseURL}/get-qnr-export/${exportId}`,
          {
            method: "GET",
            credentials: "include",
          }
        );

        if (!response.ok) {
          if (response.status === 404) {
            console.log("📭 Export not found or expired");
            localStorage.removeItem("qnr_export_id");
            return;
          }
          throw new Error("Failed to fetch export data");
        }

        const data = await response.json();

        if (data.success && data.data) {
          console.log("✅ Retrieved export data:", data);

          const mapped = mapImportedQuestions(data.data);

          if (Object.keys(mapped).length > 0) {
            setSdataGroup(mapped);
            const firstKey = Object.keys(mapped)[0];
            const first = mapped[firstKey];
            setSelectedTitle(firstKey);
            setSelectedVariable(first);
            setRows(first.rows || []);
            setColumns(first.columns || []);
            setStatements(first.statements || []);
            setOeParameters(first.oeparameters || {});

            // Clean up
            localStorage.removeItem("qnr_export_id");

            // Delete from backend (optional cleanup)
            fetch(`${apiService.baseURL}/delete-qnr-export/${exportId}`, {
              method: "DELETE",
              credentials: "include",
            }).catch((err) => console.log("Could not delete export:", err));

            toast.success(`Imported ${data.data.length} questions from QnrDesign`);
          }
        }
      } catch (e) {
        console.error("❌ Failed to import exported questionnaire", e);
        // Clean up on error
        localStorage.removeItem("qnr_export_id");
      }
    };

    importFromQnrDesign();
  }, [
    setSdataGroup,
    setSelectedTitle,
    setSelectedVariable,
    setRows,
    setColumns,
    setStatements,
    setOeParameters,
  ]);

  // Track when user starts/stops editing
  useEffect(() => {
    if (selectedTitle && isConnected) {
      emitEditingStart(selectedTitle);
      return () => {
        emitEditingEnd(selectedTitle);
      };
    }
  }, [selectedTitle, isConnected, emitEditingStart, emitEditingEnd]);

  // Track when user starts/stops editing
  useEffect(() => {
    if (selectedTitle && isConnected) {
      emitEditingStart(selectedTitle);
      return () => {
        emitEditingEnd(selectedTitle);
      };
    }
  }, [selectedTitle, isConnected, emitEditingStart, emitEditingEnd]);

  // Close settings dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showSettingsDropdown &&
        !event.target.closest(".settings-dropdown-container")
      ) {
        setShowSettingsDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSettingsDropdown]);

  const handleOpenRedirectModal = () => {
    setShowRedirectModal(true);
  };

  const handleSaveRedirect = (data) => {
    setRedirectData(data);
    // optionally show success toast
  };
  // Add this function near other handler functions
  // Update handleOpenQuotaModal:
  const handleOpenQuotaModal = () => {
    setShowQuotaModal(true);

    // Prepare initial data - ensure it's always the new structure
    let initialData = { ...quotaData };

    // If quotaData has old structure, convert it
    if (quotaData.allQuotas && !quotaData.quotas) {
      const quotas = Object.entries(quotaData.allQuotas).map(
        ([name, config]) => {
          const { allQuotas, ...cleanConfig } = config;
          return {
            ...cleanConfig,
            quotaName: name,
          };
        }
      );

      initialData = {
        quotas,
        nextQuotaNumber: quotaData.nextQuotaNumber || quotas.length + 1,
      };
    }

    // The modal will handle the rest
  };

  // Also update the exportSingleQuestion function to handle quota exports if needed

  const handleCloseQuotaModal = () => {
    setShowQuotaModal(false);
  };

  // Update handleSaveQuota to save all quotas:
  // In SurveyBuilder.js, update the handleSaveQuota function:

  const handleSaveQuota = (quotaConfig) => {
    console.log("Saving quota configuration:", quotaConfig);

    // Clean the data structure - ensure it's always an array of quotas
    let cleanQuotaData;

    if (quotaConfig.quotas && Array.isArray(quotaConfig.quotas)) {
      // New structure with quotas array
      cleanQuotaData = {
        quotas: quotaConfig.quotas,
        redirects: quotaConfig.redirects || { screenout: "", terminate: "", quotafull: "" },
        nextQuotaNumber:
          quotaConfig.nextQuotaNumber || quotaConfig.quotas.length + 1,
      };
    } else if (
      quotaConfig.allQuotas &&
      typeof quotaConfig.allQuotas === "object"
    ) {
      // Convert old structure to new structure
      const quotas = Object.entries(quotaConfig.allQuotas).map(
        ([name, config]) => {
          const { allQuotas, ...cleanConfig } = config;
          return {
            ...cleanConfig,
            quotaName: name,
          };
        }
      );

      cleanQuotaData = {
        quotas,
        redirects: quotaConfig.redirects || { screenout: "", terminate: "", quotafull: "" },
        nextQuotaNumber: quotaConfig.nextQuotaNumber || quotas.length + 1,
      };
    } else {
      // Default empty structure
      cleanQuotaData = {
        quotas: [],
        redirects: { screenout: "", terminate: "", quotafull: "" },
        nextQuotaNumber: 2,
      };
    }

    setQuotaData(cleanQuotaData);
    // setShowQuotaModal(false);
  };

  // Add this function to search variables from sdataGroup
  const searchVariables = (searchTerm) => {
    if (!searchTerm || !sdataGroup) return [];

    const results = [];
    Object.entries(sdataGroup).forEach(([key, question]) => {
      const qNumber = question.Question_number || key;
      const qTitle = question.Qtitle || "";
      const searchableText = `${qNumber} ${qTitle}`.toLowerCase();

      if (searchableText.includes(searchTerm.toLowerCase())) {
        results.push({
          id: key,
          name: qTitle,
          questionNumber: qNumber,
          type: question.type || "Unknown",
        });
      }
    });

    return results;
  };

  useEffect(() => {
    if (selectedVariable) {
      setAlphaOrder(selectedVariable.alphabeticalOrder || false);
    }
  }, [selectedVariable]);

  useEffect(() => {
    setLatestDataGroup(sdataGroup);
    console.log("Auth user:", user);
  }, [sdataGroup]);

  useEffect(() => {
    if (sdataGroup && Object.keys(sdataGroup).length > 0) {
      const needsFix = Object.entries(sdataGroup).some(
        ([key, question]) =>
          question["Question number"] && !question.Question_number
      );

      if (needsFix) {
        console.log("Fixing data inconsistency...");
        console.log("Before fix - Question 18:", sdataGroup["18"]);

        const fixedDataGroup = {};

        Object.entries(sdataGroup).forEach(([key, question]) => {
          if (question["Question number"] && !question.Question_number) {
            const { "Question number": questionNumber, ...rest } = question;
            fixedDataGroup[key] = {
              ...rest,
              Question_number: questionNumber,
            };
            console.log(
              `Fixed question ${key}: "Question number" -> "Question_number"`
            );
            console.log("After fix - Question 18:", fixedDataGroup[key]);
          } else {
            fixedDataGroup[key] = question;
          }
        });

        setSdataGroup(fixedDataGroup);
      }
    }
  }, [sdataGroup]);
  useEffect(() => {
    console.log("State changed:", {
      selectedTitle,
      selectedVariable,
      stack: new Error().stack,
    });
  }, [selectedTitle, selectedVariable]);

  useEffect(() => {
    const loadSurveyData = async () => {
      console.log("loadSurveyData called with projectId:", projectId);

      if (projectId) {
        try {
          const url = `${apiService.baseURL}/get-survey/${projectId}`;
          const response = await fetch(url);

          if (response.ok) {
            const data = await response.json();
            console.log("Received data:", data);

            if (data.sqgroup && Object.keys(data.sqgroup).length > 0) {
              setSdataGroup(data.sqgroup);

              // Set the questionnaire file ID if available
              if (data.questionnaire_file_id) {
                setQuestionnaireFileId(data.questionnaire_file_id);
              }
            }
          }
        } catch (error) {
          console.error("Error loading survey data:", error);
        }
      }
    };

    loadSurveyData();
  }, [projectId]);

  useEffect(() => {
    const organization = user?.user?.organization || user?.organization;

    if (
      organization &&
      organization.download_features &&
      Array.isArray(organization.download_features) &&
      organization.download_features.length > 0
    ) {
      setAllowedDownloads(organization.download_features);
    } else {
      console.warn("⚠️ No download features found, allowing all");
      setAllowedDownloads([
        "sawtooth",
        "pollstudio",
        "confirmit",
        "decipher_xml",
        "decipher",
        "dimension",
        "qualtrics",
        "intelli",
      ]);
    }
  }, [user]);

  const hasDownloadAccess = (downloadType) => {
    return allowedDownloads.includes(downloadType);
  };

  // Add this function with your other handlers
  const handleNavigateToQuestion = (questionKey, questionData) => {
    console.log(`🎯 Navigating to question: ${questionKey}`, questionData);

    // Update all the selected question states
    setSelectedTitle(questionKey);
    setSelectedVariable(questionData);
    setRows(questionData.rows || []);
    setColumns(questionData.columns || []);
    setStatements(questionData.statements || []);
    setOeParameters(questionData.oeparameters || {});
    setSelectedQuestionKey(questionKey);
  };

  // ========== UNDO / REDO ==========
  // Re-sync the editing panel (selected question + its rows/cols/statements)
  // after a snapshot is restored, so the UI reflects the restored data.
  const syncSelectedFromDataGroup = (restored) => {
    if (!selectedTitle) return;
    const question = restored?.[selectedTitle];
    if (!question) {
      // The selected question no longer exists in the restored snapshot.
      setSelectedTitle(null);
      setSelectedVariable({ Qtitle: "", instructions: "", rows: [] });
      setRows([]);
      setColumns([]);
      setStatements([]);
      return;
    }
    setSelectedVariable(question);
    setRows(question.rows || []);
    setColumns(question.columns || []);
    setStatements(question.statements || []);
    setOeParameters(question.oeparameters || {});
  };

  // Undo/redo restores are programmatic — don't record them in the change log.
  const setSdataGroupNoLog = (next) => {
    suppressChangelogRef.current = true;
    setSdataGroup(next);
  };

  const { undo, redo, canUndo, canRedo } = useUndoRedo({
    state: sdataGroup,
    setState: setSdataGroupNoLog,
    onRestore: syncSelectedFromDataGroup,
    resetKey: projectId,
    debounceMs: 400,
    maxHistory: 100,
  });

  // ========== CHANGE LOG (durable, shared, selective revert) ==========
  const {
    entries: changeLogEntries,
    revertEntry: revertChangeLogEntry,
    persistEntries: persistChangeLog,
    addRemoteEntry: clAddRemoteEntry,
    applyRemoteRevert: clApplyRemoteRevert,
  } = useChangeLog({
    sdataGroup,
    setSdataGroup,
    projectId,
    username: currentUsername,
    userEmail: currentUserEmail,
    suppressRef: suppressChangelogRef,
    emitChangelogAdd,
    emitChangelogRevert,
    onApplyExternal: syncSelectedFromDataGroup,
    debounceMs: 500,
  });

  // Expose the change log's remote handlers to the websocket callbacks
  // (which fire via changelogApiRef to avoid a hook ↔ hook dependency cycle).
  changelogApiRef.current.addRemoteEntry = clAddRemoteEntry;
  changelogApiRef.current.applyRemoteRevert = clApplyRemoteRevert;

  const handleRevertChange = async (uuid) => {
    const target = changeLogEntries.find((e) => e.entryUuid === uuid);
    const ok = await confirm({
      title: "Revert Change",
      message: `Revert "${target?.description || "this change"}"? Later edits that touched the same fields are kept.`,
      confirmText: "Revert",
      variant: "danger",
    });
    if (!ok) return;
    revertChangeLogEntry(uuid);
    toast.success("Change reverted.");
  };

  // Keyboard shortcuts: Ctrl/Cmd+Z = undo, Ctrl/Cmd+Y or Ctrl/Cmd+Shift+Z = redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      // If a modal (e.g. the Preview) is open, let it handle its own undo/redo.
      if (document.querySelector(".ReactModal__Overlay")) return;
      const key = e.key.toLowerCase();
      if (key === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((key === "z" && e.shiftKey) || key === "y") {
        e.preventDefault();
        redo();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [undo, redo]);

  const handleScreenOutChange = (index, checked, activeTabType) => {
    const currentItems = activeTabType === "rows" ? rows : columns;
    const updatedItems = [...currentItems];
    updatedItems[index] = { ...updatedItems[index], screenout: checked };

    if (activeTabType === "rows") {
      setRows(updatedItems);
    } else if (activeTabType === "columns") {
      setColumns(updatedItems);
    } else {
      setStatements(updatedItems);
    }

    const updatedVariable = {
      ...selectedVariable,
      [activeTabType]: updatedItems,
    };
    setSelectedVariable(updatedVariable);
    setSdataGroup((prev) => ({ ...prev, [selectedTitle]: updatedVariable }));
  };

  const deleteQuestion = async () => {
    if (selectedTitle && sdataGroup[selectedTitle]) {
      const ok = await confirm({
        title: "Delete Question",
        message: `Are you sure you want to delete "${selectedVariable.Qtitle}"? This action cannot be undone.`,
        confirmText: "Delete",
        variant: "danger",
      });

      if (ok) {
        const questionData = sdataGroup[selectedTitle];
        const updatedDataGroup = { ...sdataGroup };
        delete updatedDataGroup[selectedTitle];
        setSdataGroup(updatedDataGroup);

        setSelectedTitle(null);
        setSelectedVariable({ Qtitle: "", instructions: "", rows: [] });
        setRows([]);
        setColumns([]);

        if (isConnected) {
          emitQuestionDelete(selectedTitle, questionData);
        }

        toast.success(`Question "${selectedTitle}" deleted successfully.`);
      }
    }
  };

  const handleQuestionNumberBlur = () => {
    const newId = (selectedVariable?.Question_number || "").trim();
    const oldId = questionNumberFocusValueRef.current;

    if (!newId || newId === oldId) return;

    const collision = Object.entries(sdataGroup).some(
      ([key, q]) => key !== selectedTitle && (q.Question_number || key) === newId
    );
    if (collision) {
      toast.error(`Question ID "${newId}" is already in use. Please choose a different ID.`);
      setSelectedVariable((prev) => ({ ...prev, Question_number: oldId }));
      setSdataGroup((prev) => ({
        ...prev,
        [selectedTitle]: { ...prev[selectedTitle], Question_number: oldId },
      }));
      return;
    }

    setSdataGroup((prev) => {
      const withEdit = {
        ...prev,
        [selectedTitle]: { ...prev[selectedTitle], Question_number: newId },
      };
      const cascaded = renameQuestionReferences(withEdit, oldId, newId);

      // Re-key the question itself so sdataGroup stays keyed by Question_number
      // (consistent with how imports and quota lookups key it elsewhere).
      const { [selectedTitle]: renamedQuestion, ...rest } = cascaded;
      return { ...rest, [newId]: renamedQuestion };
    });

    setSelectedTitle(newId);
    setSelectedQuestionKey?.(newId);

    if (isConnected) {
      emitPropertyChange(selectedTitle, "Question_number", newId, oldId);
    }

    toast.success(`Question ID updated: "${oldId}" → "${newId}". All references updated.`);
  };

  const addList = (label) => {
    const questionNumber = selectedVariable?.Question_number || selectedTitle;
    const key = `${questionNumber}${label}`;
    let itemsToSave =
      label === "Rows" ? rows : label === "Columns" ? columns : statements;
    const type = label.toLowerCase();

    if (!itemsToSave || itemsToSave.length === 0) {
      toast.warning(`No ${label.toLowerCase()} to save!`);
      return;
    }

    itemsToSave = itemsToSave.filter((item) => !item.exclusive && !item.other);

    if (itemsToSave.length <= 1) {
      toast.warning(`${label} list for Question ${questionNumber} not created: Fewer than 2 valid items after filtering.`);
      return;
    }

    const itemsContent = JSON.stringify(itemsToSave);

    setList((prevList) => {
      // Check if this exact list already exists
      const existingListIndex = prevList.findIndex((item) => {
        const itemKey = Object.keys(item)[0];
        // Filter existing list data to match criteria
        const filteredItemData = item[itemKey].filter(
          (itemData) => !itemData.exclusive && !itemData.other
        );
        return JSON.stringify(filteredItemData) === itemsContent;
      });

      if (existingListIndex !== -1) {
        // List exists - append question+type if new
        const existingList = prevList[existingListIndex];
        let existingQuestionNumbers = existingList.questionNumbers || [
          existingList.questionNumber ||
          Object.keys(existingList)[0].replace(
            /Rows|Columns|Statements$/,
            ""
          ),
        ];

        // Convert old string format to objects if needed
        if (
          Array.isArray(existingQuestionNumbers) &&
          existingQuestionNumbers.length > 0 &&
          typeof existingQuestionNumbers[0] === "string"
        ) {
          existingQuestionNumbers = existingQuestionNumbers.map((num) => ({
            name: num,
            type: existingList.type || "rows",
          }));
        }

        // Check if this question+type already exists
        const existingEntry = existingQuestionNumbers.find(
          (q) => q.name === questionNumber && q.type === type
        );

        if (!existingEntry) {
          // Add the new question+type
          existingQuestionNumbers.push({ name: questionNumber, type: type });

          // Recalculate key
          const sortedQuestionNames = [
            ...new Set(existingQuestionNumbers.map((q) => q.name)),
          ].sort();
          const types = [
            ...new Set(existingQuestionNumbers.map((q) => q.type)),
          ];
          const typeIdentifier =
            types.length > 1 ? `[Mixed:${types.join("/")}]` : `[${types[0]}]`;
          const newKey =
            sortedQuestionNames.length > 1
              ? `${sortedQuestionNames.join("_")}${typeIdentifier}`
              : `${sortedQuestionNames[0]}${typeIdentifier}`;

          const updatedList = {
            [newKey]: itemsToSave, // Use filtered itemsToSave
            type: existingList.type,
            questionNumbers: existingQuestionNumbers,
            usageCount: existingQuestionNumbers.length,
            id: existingList.id, // Keep the original ID
          };

          const newList = [...prevList];
          newList[existingListIndex] = updatedList;

          const questionRefs = existingQuestionNumbers
            .map((q) => `${q.name}(${q.type})`)
            .join(", ");
          toast.success(`${label} list updated! Now used in: ${questionRefs} (${itemsToSave.length} items)`);
          return newList;
        } else {
          const questionRefs = existingQuestionNumbers
            .map((q) => `${q.name}(${q.type})`)
            .join(", ");
          toast.info(`${label} list for Question ${questionNumber} as ${type} already exists! Used in: ${questionRefs}`);
          return prevList;
        }
      } else {
        // New list - create it with meaningful ID
        const sortedQuestionNames = [questionNumber].sort();
        const types = [type];
        const typeIdentifier = `[${type}]`;
        const newKey = `${sortedQuestionNames[0]}${typeIdentifier}`;
        const newListItem = {
          [newKey]: [...itemsToSave], // Use filtered itemsToSave
          type: type,
          questionNumbers: [{ name: questionNumber, type: type }],
          usageCount: 1,
          id: generateListId(questionNumber, type), // Use question-based ID
        };

        toast.success(`${label} list for Question ${questionNumber} saved successfully! (${itemsToSave.length} items)`);
        return [...prevList, newListItem];
      }
    });
  };

  const OpenProperties = () => {
    setIsModalOpen(true);
  };

  const handleTitleChange = (property, e) => {
    let value = property === "type" ? e : e.target.value;
    const previousValue = selectedVariable[property];

    const updatedVariable = { ...selectedVariable, [property]: value };
    setSelectedVariable(updatedVariable);

    const updatedDataGroup = {
      ...sdataGroup,
      [selectedTitle]: updatedVariable,
    };
    setSdataGroup(updatedDataGroup);

    // 🔥 EMIT WEBSOCKET EVENT
    if (isConnected && selectedTitle) {
      emitPropertyChange(selectedTitle, property, value, previousValue);
    }
  };

  const handleCheckboxChange = (property, checked) => {
    const updatedVariable = { ...selectedVariable, [property]: checked };
    setSelectedVariable(updatedVariable);
    const updatedDataGroup = {
      ...sdataGroup,
      [selectedTitle]: updatedVariable,
    };
    setSdataGroup(updatedDataGroup);

    if (property === "alphabeticalOrder") {
      setAlphaOrder(checked);
    }
  };

  const addRow = () => {
    const newRow = { id: rows.length + 1, text: `` };
    setRows([...rows, newRow]);
  };

  const handleOeChange = (index, value) => {
    const oeParameters = [...oeParameters];
    const updatedVariable = { ...selectedVariable, oeParameters: oeParameters };
    setSelectedVariable(updatedVariable);
    setSdataGroup((prev) => ({ ...prev, [selectedTitle]: updatedVariable }));
  };

  const handleRowChange = (index, value) => {
    const previousValue = rows[index]?.text;
    const updatedRows = [...rows];
    updatedRows[index].text = value;
    setRows(updatedRows);

    const updatedVariable = { ...selectedVariable, rows: updatedRows };
    setSelectedVariable(updatedVariable);
    setSdataGroup((prev) => ({ ...prev, [selectedTitle]: updatedVariable }));

    // 🔥 EMIT WEBSOCKET EVENT
    if (isConnected && selectedTitle) {
      emitRowUpdate(selectedTitle, index, "rows", "text", value, previousValue);
    }
  };

  // Updated cleanPastedHTML function with list support
  const cleanPastedHTML = (dirty) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(dirty, "text/html");

    // Allowed tags - Added OL, UL, LI for list support
    const allowedTags = [
      "B",
      "I",
      "U",
      "STRONG",
      "EM",
      "BR",
      "P",
      "DIV",
      "OL",
      "UL",
      "LI",
      "SPAN",
    ];

    // Allowed style properties for colors and formatting
    const allowedStyles = [
      "color",
      "background-color",
      "font-weight",
      "font-style",
      "text-decoration",
    ];

    const cleanNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent;
      }

      if (!allowedTags.includes(node.nodeName)) {
        return Array.from(node.childNodes).map(cleanNode).join("");
      }

      // Rebuild safe element
      const el = document.createElement(node.nodeName.toLowerCase());

      // 🟢 NEW: Preserve allowed style attributes (for colors)
      if (node.style && node.style.length > 0) {
        const styles = [];
        allowedStyles.forEach((styleProp) => {
          const value = node.style.getPropertyValue(styleProp);
          if (value) {
            styles.push(`${styleProp}: ${value}`);
          }
        });
        if (styles.length > 0) {
          el.setAttribute("style", styles.join("; "));
        }
      }

      node.childNodes.forEach((child) => {
        el.innerHTML += cleanNode(child);
      });
      return el.outerHTML;
    };

    return Array.from(doc.body.childNodes).map(cleanNode).join("");
  };

  const handlePaste = (e, type, directMode = null) => {
    e.preventDefault();

    // Get HTML or plain text
    let pastedHTML =
      e.clipboardData.getData("text/html") ||
      e.clipboardData.getData("text/plain");

    // If we got plain text, convert newlines to <br> tags
    if (!e.clipboardData.getData("text/html")) {
      pastedHTML = pastedHTML.replace(/\r?\n/g, "<br>");
    }

    // Clean the HTML but preserve formatting tags
    pastedHTML = cleanPastedHTML(pastedHTML);
    console.log("After cleanPastedHTML:");
    console.log(pastedHTML);

    // Create a temporary div to parse the HTML
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = pastedHTML;

    let textLines = [];

    // 🟩 Check if this is a Word-generated list (has <li> elements within <ol> or <ul>)
    const listItems = tempDiv.querySelectorAll("li");
    const isWordList =
      listItems.length > 0 &&
      (tempDiv.querySelector("ol") || tempDiv.querySelector("ul"));

    console.log("Number of <li> elements found:", listItems.length);
    console.log("Is Word-generated list:", isWordList);

    if (isWordList) {
      listItems.forEach((li, idx) => {
        console.log(`--- List Item ${idx + 1} ---`);
        console.log("innerHTML:", li.innerHTML);
        console.log("textContent:", li.textContent);
      });

      // 🟢 For Word lists: numbering is automatic (via <ol>), so content doesn't include numbers
      // Just extract innerHTML directly - no number removal needed
      textLines = Array.from(listItems)
        .map((li) => {
          return li.innerHTML.trim();
        })
        .filter((line) => line && line !== "");

      // Check for content after the list (like "None of these")
      const listParent = listItems[0].closest("ol, ul");
      if (listParent && listParent.parentElement) {
        // Get all nodes after the list
        let nextSibling = listParent.nextSibling;
        while (nextSibling) {
          if (nextSibling.nodeType === Node.TEXT_NODE) {
            const text = nextSibling.textContent.trim();
            if (text) {
              textLines.push(text);
            }
          } else if (nextSibling.nodeType === Node.ELEMENT_NODE) {
            const content = nextSibling.innerHTML.trim();
            if (content) {
              textLines.push(content);
            }
          }
          nextSibling = nextSibling.nextSibling;
        }
      }
    } else {
      // 🟨 Handle <br> or block-based text otherwise
      const rootContent = tempDiv.innerHTML.trim();

      if (rootContent.includes("<br")) {
        textLines = rootContent
          .split(/<br\s*\/?>/gi)
          .map((line) => line.trim())
          .filter((line) => line && line !== "");
      } else {
        const blockElements = tempDiv.querySelectorAll("p, div, tr, td");
        if (blockElements.length > 0) {
          const leafElements = Array.from(blockElements).filter((element) => {
            const hasNestedBlocks =
              element.querySelectorAll("p, div, tr, td").length > 0;
            return !hasNestedBlocks;
          });

          leafElements.forEach((element) => {
            const content = element.innerHTML.trim();
            if (!content) return;

            if (content.includes("<br")) {
              const subLines = content
                .split(/<br\s*\/?>/gi)
                .map((line) => line.trim())
                .filter((line) => line && line !== "");
              textLines.push(...subLines);
            } else {
              textLines.push(content);
            }
          });

          // ✨ NEW: After processing block elements, check for any remaining direct children
          // that aren't block elements (like standalone <span> tags)
          Array.from(tempDiv.childNodes).forEach((child) => {
            // Skip if it's a block element we already processed
            if (
              child.nodeType === Node.ELEMENT_NODE &&
              ["P", "DIV", "TR", "TD"].includes(child.nodeName)
            ) {
              return;
            }

            // Process text nodes and inline elements
            if (child.nodeType === Node.TEXT_NODE) {
              const text = child.textContent.trim();
              if (text) {
                textLines.push(text);
              }
            } else if (child.nodeType === Node.ELEMENT_NODE) {
              const content = child.innerHTML.trim();
              if (content) {
                textLines.push(content);
              }
            }
          });
        } else {
          const textContent = tempDiv.textContent || tempDiv.innerText || "";
          const lines = textContent
            .split(/\r?\n/)
            .filter((line) => line.trim());
          textLines = lines.length > 1 ? lines : [rootContent];
        }
      }
    }

    console.log("Parsed text lines (after list processing):", textLines);

    // 🟦 ONLY merge separate number lines if this is NOT a Word list
    // (Word lists don't have separate number lines since numbering is automatic)
    const mergedLines = [];
    if (!isWordList) {
      for (let i = 0; i < textLines.length; i++) {
        const currentLine = textLines[i];
        const plainText = currentLine.replace(/<[^>]*>/g, "").trim();

        if (/^\d+$/.test(plainText) && i + 1 < textLines.length) {
          mergedLines.push(`${currentLine}`);

        } else {
          mergedLines.push(currentLine);
        }
      }
    } else {
      // For Word lists, don't merge - lines are already correct
      mergedLines.push(...textLines);
    }

    console.log("Merged lines:", mergedLines);

    // 🧩 Clean numbering from each line
    // For Word lists: don't remove numbers from content (there aren't any)
    // For manual lists: remove leading numbers only after checking if row is completely numeric
    const newItems = mergedLines.map((line, index) => {
      const lineDiv = document.createElement("div");
      lineDiv.innerHTML = line;

      const plainText = lineDiv.textContent || lineDiv.innerText || "";

      // 🔴 NEW CHECK: If the entire row content is numeric, don't remove anything
      const isCompletelyNumeric = /^\d+$/.test(plainText);

      // Check if this line has a number at the start
      const numberMatch = plainText.match(/^(\d+)[\s\t\.\):\-]*/);

      let extractedIndex;
      let displayText = line;

      // MODIFIED: Check removeNumber property before processing number removal
      // ONLY process number removal if:
      // 1. removeNumber is true, AND
      // 2. This is NOT a Word list, AND
      // 3. The row is NOT completely numeric (if it's completely numeric, preserve the number)
      if (removeNumber && numberMatch && !isWordList && !isCompletelyNumeric) {
        // Found a manual number and row is not completely numeric - extract it
        extractedIndex = numberMatch[1];

        console.log(
          `Line ${index}: Found manual number "${numberMatch[0]}" in "${plainText}"`
        );

        // Remove ALL leading spans that contain only the number and whitespace
        // This handles Word's complex nested span structure
        while (lineDiv.firstChild && lineDiv.firstChild.nodeName === "SPAN") {
          const firstSpan = lineDiv.firstChild;
          const spanText = (firstSpan.textContent || "").trim();

          // If the span contains only the number and punctuation/whitespace, remove it entirely
          if (/^\d+[\.\):\-]*$/.test(spanText) || spanText === "") {
            console.log(`Removing span with content: "${spanText}"`);
            firstSpan.remove();
          } else {
            // Check if this span's text starts with the number
            const spanFullText = firstSpan.textContent || "";
            if (spanFullText.trim().startsWith(numberMatch[0])) {
              // Number is at the start of this span's text - need to clean inside
              break;
            } else {
              // This span doesn't contain the number, stop checking
              break;
            }
          }
        }

        // If number still remains in the text, use the tree walker approach
        const currentText = lineDiv.textContent || "";
        if (currentText.trim().match(/^(\d+)[\s\t\.\):\-]*/)) {
          const walker = document.createTreeWalker(
            lineDiv,
            NodeFilter.SHOW_TEXT,
            null,
            false
          );

          let node;
          while ((node = walker.nextNode())) {
            const nodeText = node.nodeValue;
            if (nodeText && nodeText.trim()) {
              const nodeNumberMatch = nodeText.match(/^(\d+)[\s\t\.\):\-]*/);

              if (nodeNumberMatch) {
                node.nodeValue = nodeText.substring(nodeNumberMatch[0].length);
                console.log(`Removed "${nodeNumberMatch[0]}" from text node`);
                break;
              }
            }
          }
        }

        displayText = lineDiv.innerHTML.trim();

        // Additional cleanup: remove leading &nbsp; entities and whitespace from the HTML
        displayText = displayText.replace(/^(&nbsp;|\s)+/, "");

        // Also clean up any remaining empty spans at the start
        const cleanDiv = document.createElement("div");
        cleanDiv.innerHTML = displayText;

        // Remove empty leading spans
        while (
          cleanDiv.firstChild &&
          cleanDiv.firstChild.nodeName === "SPAN" &&
          !cleanDiv.firstChild.textContent.trim()
        ) {
          cleanDiv.firstChild.remove();
        }

        displayText = cleanDiv.innerHTML.trim();
      } else if (isCompletelyNumeric) {
        // 🔴 NEW: Row is completely numeric - preserve the number in the display text
        extractedIndex = String(index + 1);
        console.log(
          `Line ${index}: Completely numeric row - preserving original number "${plainText}"`
        );
        // Keep the original line as displayText (don't remove the number)
        displayText = line;
      } else {
        // No manual number found OR this is a Word list - use sequential index
        extractedIndex = String(index + 1);
        if (!isWordList) {
          console.log(
            `Line ${index}: No manual number found, using index ${extractedIndex}`
          );
        } else {
          console.log(
            `Line ${index}: Word list - using sequential index ${extractedIndex}`
          );
        }
      }

      // Final trim of the display text to remove any leading/trailing whitespace
      const finalDiv = document.createElement("div");
      finalDiv.innerHTML = displayText;
      const finalText = (
        finalDiv.textContent ||
        finalDiv.innerText ||
        ""
      ).trim();

      // If the plain text is clean but HTML still has leading whitespace, rebuild
      if (finalText && displayText.trim() !== displayText) {
        displayText = displayText.trim();
      }

      // One more pass to clean leading non-breaking spaces from the actual content
      displayText = displayText.replace(/^(<[^>]+>)*(&nbsp;|\s)+/, "$1");

      return {
        index: extractedIndex,
        text: displayText,
      };
    });

    console.log("New items created:", newItems);
    console.log("===========================================");

    // If directMode is provided or skipModal flag is set (from text box), apply directly without modal
    if (directMode || e.skipModal) {
      const modeToUse = directMode || e.pasteMode || "append";
      // Add insertIndex to newItems if provided
      if (e.insertIndex !== undefined && newItems.length > 0) {
        newItems[0].insertIndex = e.insertIndex;
      }
      // Pass the preserveOriginalIndices flag to applyPaste
      applyPaste(modeToUse, type, newItems, e.preserveOriginalIndices || false);
    } else {
      // Set pendingPaste and show the modal
      setPendingPaste({ type, newItems });
      setPasteModalOpen(true);
    }
  };
  const applyPaste = (mode, type = null, items = null, preserveIndices = false) => {
    // Use provided parameters or fall back to pendingPaste
    const pasteType = type || pendingPaste?.type;
    const newItems = items || pendingPaste?.newItems;

    if (!newItems) return;

    // Process newItems
    const processItems = (items) => {
      const processedItems = [];

      items.forEach((item) => {
        if (item.text && typeof item.text === "string") {
          const trimmedText = item.text.trim();

          // Only add if there's actual content
          if (trimmedText !== "") {
            processedItems.push({
              ...item,
              text: trimmedText,
            });
          }
        } else if (item.text) {
          // If text exists but not string, keep as is
          processedItems.push({
            ...item,
          });
        }
      });

      return processedItems;
    };

    const processedNewItems = processItems(newItems);

    // Get current items based on type
    const currentItems =
      pasteType === "rows"
        ? rows
        : pasteType === "columns"
          ? columns
          : statements;

    let updatedItems;
    let insertIndex;
    const getMaxIndex = (items) => {
      if (!items || items.length === 0) return 0;

      // Try to parse indices as numbers, handling both string and number formats
      const indices = items.map(item => {
        const idx = item.index || item.id || 0;
        // If it's a string like "r1" or "c1", extract the number
        if (typeof idx === 'string') {
          const match = idx.match(/\d+/);
          return match ? parseInt(match[0], 10) : 0;
        }
        return parseInt(idx, 10) || 0;
      });

      return Math.max(...indices, 0);
    };

    // If preserveOriginalIndices is true, keep the original indices from pasted items
    if (preserveIndices) {
      const maxPastedIndex = getMaxIndex(currentItems);
      const shiftedCurrentItems = processedNewItems.map(item => {
        const newItem = { ...item };
        // Shift existing indices by the max pasted index
        if (newItem.index) {
          const numMatch = String(newItem.index).match(/\d+/);
          if (numMatch) {
            const num = parseInt(numMatch[0], 10);
            newItem.index = String(newItem.index).replace(/\d+/, num + maxPastedIndex);
          }
        }
        return newItem;
      });

      // Just combine the items without reindexing
      if (mode === "replace") {
        updatedItems = shiftedCurrentItems;
      } else if (mode === "prepend") {
        updatedItems = [...shiftedCurrentItems, ...currentItems];
      } else if (mode === "insertBefore") {
        insertIndex = items?.[0]?.insertIndex ?? 0;
        updatedItems = [
          ...currentItems.slice(0, insertIndex),
          ...shiftedCurrentItems,
          ...currentItems.slice(insertIndex),
        ];
      } else if (mode === "insertAfter") {
        insertIndex = items?.[0]?.insertIndex ?? currentItems.length - 1;
        updatedItems = [
          ...currentItems.slice(0, insertIndex + 1),
          ...shiftedCurrentItems,
          ...currentItems.slice(insertIndex + 1),
        ];
      }
      else if (mode === "replacerc") {
        insertIndex = items?.[0]?.insertIndex ?? currentItems.length - 1;
        updatedItems = [
          ...currentItems.slice(0, insertIndex),
          ...shiftedCurrentItems,
          ...currentItems.slice(insertIndex + 1),
        ];
      }

      else {
        // Append mode
        updatedItems = [...currentItems, ...shiftedCurrentItems];
      }

      // No reindexing - keep original indices
      console.log("Preserving original indices from paste");
    } else {
      // Original reindexing logic
      if (mode === "replace") {
        // For replace mode: keep the indices as they are from paste
        updatedItems = processedNewItems;
      } else if (mode === "prepend") {
        // PREPEND: Add at the beginning
        // New items get indices 1, 2, 3... (in order)
        // Existing items get shifted by the number of new items

        // First, assign temporary indices to new items based on their order
        const itemsWithTempIndices = processedNewItems.map((item, idx) => ({
          ...item,
          tempIndex: idx + 1, // Temporary index for ordering
        }));

        // Combine with existing items
        const combined = [...itemsWithTempIndices, ...currentItems];

        // Reassign final indices based on final order
        updatedItems = combined.map((item, idx) => ({
          ...item,
          index: String(idx + 1), // Final index based on position
          // Remove tempIndex if it exists
          ...(item.tempIndex !== undefined && { tempIndex: undefined }),
        }));
      } else if (mode === "insertBefore") {
        // INSERT BEFORE: Insert at a specific position before the target
        insertIndex = items?.[0]?.insertIndex ?? 0;

        // Create items with temporary indices for ordering
        const itemsWithTempIndices = processedNewItems.map((item, idx) => ({
          ...item,
          tempIndex: idx + 1,
        }));

        // Combine with existing items at the insert position
        const combined = [
          ...currentItems.slice(0, insertIndex),
          ...itemsWithTempIndices,
          ...currentItems.slice(insertIndex),
        ];

        // Reassign final indices based on final order
        updatedItems = combined.map((item, idx) => ({
          ...item,
          index: String(idx + 1), // Final index based on position
          ...(item.tempIndex !== undefined && { tempIndex: undefined }),
        }));
      } else if (mode === "insertAfter") {
        // INSERT AFTER: Insert at a specific position after the target
        insertIndex = items?.[0]?.insertIndex ?? currentItems.length - 1;

        // Create items with temporary indices for ordering
        const itemsWithTempIndices = processedNewItems.map((item, idx) => ({
          ...item,
          tempIndex: idx + 1,
        }));

        // Combine with existing items after the insert position
        const combined = [
          ...currentItems.slice(0, insertIndex + 1),
          ...itemsWithTempIndices,
          ...currentItems.slice(insertIndex + 1),
        ];

        // Reassign final indices based on final order
        updatedItems = combined.map((item, idx) => ({
          ...item,
          index: String(idx + 1), // Final index based on position
          ...(item.tempIndex !== undefined && { tempIndex: undefined }),
        }));
      }
      else if (mode === "replacerc") {
        // INSERT AFTER: Insert at a specific position after the target
        insertIndex = items?.[0]?.insertIndex ?? currentItems.length - 1;

        // Create items with temporary indices for ordering
        const itemsWithTempIndices = processedNewItems.map((item, idx) => ({
          ...item,
          tempIndex: idx + 1,
        }));

        // Combine with existing items after the insert position
        const combined = [
          ...currentItems.slice(0, insertIndex),
          ...itemsWithTempIndices,
          ...currentItems.slice(insertIndex + 1),
        ];

        // Reassign final indices based on final order
        updatedItems = combined.map((item, idx) => ({
          ...item,
          index: String(idx + 1), // Final index based on position
          ...(item.tempIndex !== undefined && { tempIndex: undefined }),
        }));
      } else {
        // APPEND: Add at the end
        // Existing items keep their current indices (1 to n)
        // New items get indices starting from n+1, n+2, etc.

        // First, calculate starting index for new items
        const startIndex = currentItems.length;

        // Assign new indices to pasted items
        const itemsWithNewIndices = processedNewItems.map((item, idx) => ({
          ...item,
          index: String(startIndex + idx + 1),
        }));

        // Keep existing items as they are (their indices are already correct)
        updatedItems = [...currentItems, ...itemsWithNewIndices];
      }
    }

    // Remove any temporary properties that might have been added
    updatedItems = updatedItems.map(item => {
      const { tempIndex, ...cleanItem } = item;
      return cleanItem;
    });

    if (pasteType === "rows") {
      setRows(updatedItems);
      setSelectedVariable({ ...selectedVariable, rows: updatedItems });
    } else if (pasteType === "columns") {
      setColumns(updatedItems);
      setSelectedVariable({ ...selectedVariable, columns: updatedItems });
    } else if (pasteType === "statements") {
      setStatements(updatedItems);
      setSelectedVariable({ ...selectedVariable, statements: updatedItems });
    }

    setSdataGroup((prev) => ({
      ...prev,
      [selectedTitle]: { ...selectedVariable, [pasteType]: updatedItems },
    }));

    if (isConnected && selectedTitle) {
      emitBulkRowsAdd(selectedTitle, processedNewItems, pasteType, mode);
    }

    if (pendingPaste) {
      setPasteModalOpen(false);
      setPendingPaste(null);
    }
  };
  const addadditionalRow = (label) => {
    let newItem;
    let updatedItems;
    let rowType = label.toLowerCase();

    if (label === "Rows") {
      newItem = { index: `${rows ? rows.length + 1 : 1}`, text: `` };
      updatedItems = [...rows, newItem];
      setRows([...rows, newItem]);
    } else if (label === "Columns") {
      newItem = { index: `${columns ? columns.length + 1 : 1}`, text: `` };
      updatedItems = [...columns, newItem];
      setColumns([...columns, newItem]);
    } else if (label === "Statements") {
      newItem = {
        index: `${statements ? statements.length + 1 : 1}`,
        text: ``,
      };
      updatedItems = [...statements, newItem];
      setStatements([...statements, newItem]);
    }

    setSelectedVariable({
      ...selectedVariable,
      [rowType]: updatedItems,
    });
    setSdataGroup((prev) => ({
      ...prev,
      [selectedTitle]: {
        ...selectedVariable,
        [rowType]: updatedItems,
      },
    }));

    // 🔥 ADD THIS AT THE END
    if (isConnected && selectedTitle) {
      emitRowAdd(selectedTitle, newItem, rowType, updatedItems.length - 1);
    }
  };

  const savePropertyChanges = (rows, label) => {
    if (label === "Rows") {
      setRows(rows);
    } else if (label === "Columns") {
      setColumns(rows);
    } else if (label === "Statements") {
      setStatements(rows);
    } else if (label === "oeParameters") {
      setOeParameters(rows);
    }
    setSelectedVariable({ ...selectedVariable, [label.toLowerCase()]: rows });
    setSdataGroup((prev) => ({
      ...prev,
      [selectedTitle]: { ...selectedVariable, [label.toLowerCase()]: rows },
    }));
  };
  const keyOf = (q) => q.Question_number;

  const handleQuotaUpsert = (src, q) => {
    setSdataGroup(prev => {
      const next = {};
      Object.entries(prev).forEach(([k, item]) => {
        next[k] = (typeof item._order === "number" && item._order > src._order)
          ? { ...item, _order: item._order + 1 }
          : item;
      });
      next[keyOf(q)] = q;
      return next;
    });
  };

  const handleQuotaRemove = (sourceNumber) => {
    const quotaNum = `${sourceNumber}_HIDDEN_QUOTA`;
    setSdataGroup(prev =>
      Object.fromEntries(
        Object.entries(prev).filter(([, item]) => item.Question_number !== quotaNum)
      )
    );
  };

  const deleteRow = (index) => {
    const rowToDelete = rows[index]; // 🔥 CAPTURE BEFORE DELETION
    const updatedRows = rows.filter((_, i) => i !== index);
    setRows(updatedRows);

    setSelectedVariable({ ...selectedVariable, rows: updatedRows });
    setSdataGroup((prev) => ({
      ...prev,
      [selectedTitle]: { ...selectedVariable, rows: updatedRows },
    }));

    // 🔥 ADD THIS AT THE END
    if (isConnected && selectedTitle) {
      emitRowDelete(selectedTitle, index, "rows", rowToDelete);
    }
  };
  const deleteCol = (index) => {
    const updatedRows = columns.filter((_, i) => i !== index);
    setColumns(updatedRows);

    setSelectedVariable({ ...selectedVariable, columns: updatedRows });
    setSdataGroup((prev) => ({
      ...prev,
      [selectedTitle]: { ...selectedVariable, columns: updatedRows },
    }));
  };
  const deleteStatements = (index) => {
    const updatedRows = columns.filter((_, i) => i !== index);
    setStatements(updatedRows);

    setSelectedVariable({ ...selectedVariable, statements: updatedRows });
    setSdataGroup((prev) => ({
      ...prev,
      [selectedTitle]: { ...selectedVariable, statements: updatedRows },
    }));
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const updatedRows = Array.from(rows);
    const [movedRow] = updatedRows.splice(result.source.index, 1);
    updatedRows.splice(result.destination.index, 0, movedRow);
    setRows(updatedRows);

    setSelectedVariable({ ...selectedVariable, rows: updatedRows });
    setSdataGroup((prev) => ({
      ...prev,
      [selectedTitle]: { ...selectedVariable, rows: updatedRows },
    }));
  };

  const handleFlip = () => {
    const tempRows = [...rows];
    const tempColumns = [...columns];

    setRows(tempColumns);
    setColumns(tempRows);

    const updatedVariable = {
      ...selectedVariable,
      rows: tempColumns,
      columns: tempRows,
    };

    setSelectedVariable(updatedVariable);
    setSdataGroup((prev) => ({
      ...prev,
      [selectedTitle]: updatedVariable,
    }));
  };

  const handleSaveSurvey = async () => {
    try {
      // const normalized = validateSurveyData(sdataGroup);
      //   setSdataGroup(normalized); // optional visual update

      const normalizedData = normalizeRequiredFlags(sdataGroup);
      setSdataGroup(normalizedData); // keep in-memory state consistent with what's saved

      const response = await fetch(`${apiService.baseURL}/save-survey`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          projectId,
          sdataGroup: normalizedData,
          client: JSON.stringify(selectedClient),
          project: JSON.stringify(selectedProject),
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to save survey");
      }

      const result = await response.json();
      // Persist the change log alongside the survey so it survives reloads.
      await persistChangeLog();
      toast.success("Survey saved successfully!");
    } catch (error) {
      toast.error("Failed to save survey. Please try again.");
    }
  };

  const handleQualtrics = () => {
    setSdataGroup((prevDataGroup) => {
      const updatedDataGroup = { ...prevDataGroup };
      console.log("Updated data being sent to Qualtrics:", updatedDataGroup);

      fetch(`${apiService.baseURL}/generate-titles-qualtrics`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          projectId,
          dataList: updatedDataGroup,
          banners,
          project: JSON.stringify(selectedProject),
          client: JSON.stringify(selectedClient),
          autoPreQuotes,
          columnIndex,
          alphaOrder,
        }),
      })
        .then((response) => response.blob())
        .then((blob) => {
          toast.success("Qualtrics survey generated successfully!");
        })
        .catch((error) => {
          console.error("Error:", error);
        });

      return updatedDataGroup;
    });
  };

  // Add this helper function at the top of your component or before handleAddTable
  const stripHtmlTags = (html) => {
    if (typeof html !== "string") return html;
    return html.replace(/<[^>]*>/g, "").trim();
  };

  const formatVarNameForTool = (question, questionNumber, tool) => {
    const varName = [];

    // Helper function to format grid variable names
    const formatGridVarName = (rowIndex, colIndex = null) => {
      const baseName = questionNumber;

      if (tool === "decipher") {
        // For Decipher: q1r1c1 for grids with rows and columns, q1r1 for grids with only rows
        if (colIndex !== null) {
          return `${baseName}r${rowIndex}c${colIndex}`;
        } else {
          return `${baseName}r${rowIndex}`;
        }
      } else if (tool === "spss") {
        // For SPSS Dimensions: Grid_q1_1_1 for grids with rows and columns, Grid_q1_1 for grids with only rows
        if (colIndex !== null) {
          return `Grid_${baseName}_${rowIndex}_${colIndex}`;
        } else {
          return `Grid_${baseName}_${rowIndex}`;
        }
      } else {
        // For other tools: keep as is (q1_1_1 or q1_1)
        if (colIndex !== null) {
          return `${baseName}_${rowIndex}_${colIndex}`;
        } else {
          return `${baseName}_${rowIndex}`;
        }
      }
    };

    // Determine question type
    const questionType = question.type || "";

    if (questionType === "Single Response" || questionType === "Numeric") {
      // Single variable - no changes needed
      varName.push({ Name: questionNumber });
    } else if (
      questionType === "MultipleResponse" ||
      questionType === "Multiple Response" ||
      questionType === "SingleGrid" ||
      questionType === "Single Grid" ||
      questionType === "NumericGrid"
    ) {
      // Grid with only rows
      if (question.rows && Array.isArray(question.rows)) {
        question.rows.forEach((row, index) => {
          const rowIndex = row.index || index + 1;
          const formattedName = formatGridVarName(rowIndex);
          varName.push({
            Name: formattedName,
            row: rowIndex,
          });
        });
      }
    } else if (
      questionType === "MultiGrid" ||
      questionType === "Single2DGrid" ||
      questionType === "Numeric2DGrid"
    ) {
      // Grid with rows and columns
      if (
        question.rows &&
        question.columns &&
        Array.isArray(question.rows) &&
        Array.isArray(question.columns)
      ) {
        question.rows.forEach((row, rIndex) => {
          const rowIndex = row.index || rIndex + 1;
          question.columns.forEach((column, cIndex) => {
            const colIndex = column.index || cIndex + 1;
            const formattedName = formatGridVarName(rowIndex, colIndex);
            varName.push({
              Name: formattedName,
              row: rowIndex,
              col: colIndex,
            });
          });
        });
      }
    }

    return varName;
  };

  const handleAddTable = () => {
    setToolSelectionModalOpen(true);
  };

  const handleAddTableSuccess = (data) => {
    setSdataGroup(data);
    toast.success("Table transformed and saved successfully!");
  };

  const handleSwitchBetween = (from, to) => {
    const fromItems =
      from === "rows" ? rows : from === "columns" ? columns : statements;
    const toItems =
      to === "rows" ? rows : to === "columns" ? columns : statements;

    // Set the 'from' items to 'to' items
    if (from === "rows") setRows(toItems);
    else if (from === "columns") setColumns(toItems);
    else setStatements(toItems);

    // Set the 'to' items to 'from' items
    if (to === "rows") setRows(fromItems);
    else if (to === "columns") setColumns(fromItems);
    else setStatements(fromItems);

    // Update selectedVariable and sdataGroup
    const updatedVariable = {
      ...selectedVariable,
      [from]: toItems,
      [to]: fromItems,
    };

    setSelectedVariable(updatedVariable);
    setSdataGroup((prev) => ({
      ...prev,
      [selectedTitle]: updatedVariable,
    }));
  };

  const exportSingleQuestion = async (exportType) => {
    if (!selectedTitle || !selectedVariable) {
      toast.warning("Please select a question first");
      return;
    }

    // Create a data object with only the selected question
    const singleQuestionData = {
      [selectedTitle]: selectedVariable,
    };

    let endpoint = "";
    let title = "";

    switch (exportType) {
      case "DC":
        endpoint = `${apiService.baseURL}/generate-titles`;
        title = "Decipher XML Code";
        break;
      case "DM":
        endpoint = `${apiService.baseURL}/generate-titles-dimension`;
        title = "Dimension Code";
        break;
      case "QT":
        endpoint = `${apiService.baseURL}/generate-titles-qualtrics`;
        title = "Qualtrics Code";
        break;
      default:
        return;
    }

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          projectId,
          dataList: singleQuestionData,
          banners,
          project: JSON.stringify(selectedProject),
          client: JSON.stringify(selectedClient),
          type: exportType === "DC" ? undefined : exportType,
          columnIndex,
          autoPreQuotes,
          alphaOrder,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to generate code");
      }

      const code = await response.text();
      setGeneratedCode(code);
      setCodeDialogTitle(title);
      setCodeDialogOpen(true);
    } catch (error) {
      console.error("Error generating code:", error);
      toast.error("Failed to generate code. Please try again.");
    }
  };

  const copyCodeToClipboard = () => {
    navigator.clipboard
      .writeText(generatedCode)
      .then(() => {
        toast.success("Code copied to clipboard!");
      })
      .catch(() => {
        toast.error("Failed to copy code");
      });
  };

  const handleMatchRows = () => {
    if (!matchQuestionNumber.trim()) {
      toast.warning("Please enter question number(s) to match");
      return;
    }

    if (!rows || rows.length === 0) {
      toast.warning("Current question has no rows to match against");
      return;
    }

    let tokenset = [];
    let matchGrid = false;
    const normalizeText = (text) =>
      (text || "").toString().trim().toLowerCase().replace(/\s+/g, " ");

    // Extract bare question references (doesn't include constraints)
    const extractQuestionRefs = (expression) => {
      const tokens = expression
        .split(/(\b(?:and|or|not)\b|[,()])/i)
        .map((t) => t && t.trim())
        .filter((t) => t && !["and", "or", "not"].includes(t.toLowerCase()));

      const questionRefs = [];
      tokens.forEach((token) => {
        // token might be like "FAMILIAR_FRANCHISE=3,4" or "AIDAWARE_FRANCHISE"
        const qMatch = token.match(/^([A-Za-z0-9_]+)/);
        tokenset.push(token);
        if (qMatch && !questionRefs.includes(qMatch[1])) {
          questionRefs.push(qMatch[1]);
        }
      });

      return questionRefs;
    };

    // Find matching rows for a given currentRow and targetQuestion.
    // Returns array of rowIndex numbers (integers).
    const findMatchingRows = (currentRow, targetQuestion, constraints = "") => {
      const currentText = normalizeText(currentRow.text || "");
      const matches = [];

      // Parse constraints (rows=, cols=) into arrays of numbers
      let rowFilter = null,
        colFilter = null;
      if (constraints) {
        const rowMatch = constraints.match(/rows?=([\d\-,]+)/i);
        const colMatch = constraints.match(/cols?=([\d\-,]+)/i);
        if (rowMatch) {
          rowFilter = rowMatch[1].split(",").flatMap((part) =>
            part.includes("-")
              ? Array.from(
                {
                  length:
                    parseInt(part.split("-")[1], 10) -
                    parseInt(part.split("-")[0], 10) +
                    1,
                },
                (_, i) => parseInt(part.split("-")[0], 10) + i
              )
              : [parseInt(part, 10)]
          );
        }
        if (colMatch) {
          colFilter = colMatch[1].split(",").flatMap((part) =>
            part.includes("-")
              ? Array.from(
                {
                  length:
                    parseInt(part.split("-")[1], 10) -
                    parseInt(part.split("-")[0], 10) +
                    1,
                },
                (_, i) => parseInt(part.split("-")[0], 10) + i
              )
              : [parseInt(part, 10)]
          );
        }
      }

      targetQuestion.rows?.forEach((targetRow, idx) => {
        const targetText = normalizeText(targetRow.text || "");
        // row index fallback: if targetRow.index exists use it else use idx+1
        const rowIndex = targetRow.index ?? idx + 1;

        // Apply row/col filters if provided
        if (rowFilter && !rowFilter.includes(rowIndex)) return;

        // If grid and targetRow may have col/colIndex, apply col filter check
        if (colFilter) {
          // If targetRow has explicit col/colIndex we can check; otherwise skip check
          const colIndex = targetRow.col ?? targetRow.colIndex;
          if (typeof colIndex !== "undefined" && !colFilter.includes(colIndex))
            return;
        }

        // exact match text (you used exact match in original)
        if (currentText === targetText) {
          matches.push(rowIndex); // store number only; build string later
        }
      });

      if (matches.length === 0) {
        targetQuestion.columns?.forEach((targetRow, idx) => {
          const targetText = normalizeText(targetRow.text || "");
          // row index fallback: if targetRow.index exists use it else use idx+1
          const rowIndex = targetRow.index ?? idx + 1;

          // Apply row/col filters if provided
          if (rowFilter && !rowFilter.includes(rowIndex)) return;

          // If grid and targetRow may have col/colIndex, apply col filter check
          if (colFilter) {
            // If targetRow has explicit col/colIndex we can check; otherwise skip check
            const colIndex = targetRow.col ?? targetRow.colIndex;
            if (
              typeof colIndex !== "undefined" &&
              !colFilter.includes(colIndex)
            )
              return;
          }

          // exact match text (you used exact match in original)
          if (currentText === targetText) {
            matches.push(rowIndex); // store number only; build string later
            matchGrid = true;
          }
        });
      }
      return matches;
    };

    // Build final logic string by replacing tokens like:
    // - "Q1" or "Q1=3,4" with appropriate match text
    // We pass questionsInfo map which gives isGrid and constraint for each qNum
    const buildLogicWithRowNumbers = (expression, questionsInfo) => {
      if (!expression) return "";

      let logic = expression;

      // For each question in questionsInfo, replace occurrences of QNUM and optional constraint
      Object.keys(questionsInfo).forEach((qNum) => {
        const info = questionsInfo[qNum];
        const matches = info.matches; // array of rowIndex numbers
        const originalConstraint = info.constraintRaw || ""; // like "=3,4" or ""
        const isGrid = !!info.isGrid;

        // Regex to match the token: question name followed by any non-space non-paren chunk (the constraint)
        // We will replace the whole token (e.g. "FAMILIAR_FRANCHISE=3,4") to avoid leftover "=3,4".
        const tokenRegex = new RegExp(`\\b${qNum}([^\\s()]*)`, "g");

        if (matches && matches.length > 0) {
          // Build replacement: for grid -> QNUM=r{row}={cols if present}
          // for non-grid -> QNUM={row}
          const parts = matches.map((r) => {
            if (!matchGrid && isGrid) {
              // keep the original constraint (if present); originalConstraint may be "=3,4"
              return `${qNum} r${r}${originalConstraint}`;
            } else {
              return `${expression}=${r}`;
            }
            return `${qNum}=${r}`;
          });
          if (matchGrid) {
            const replacement =
              parts.length > 1 ? `(${parts.join(" or ")})` : parts[0];
            logic = replacement;
          } else {
            const replacement =
              parts.length > 1 ? `(${parts.join(" or ")})` : parts[0];
            logic = logic.replace(tokenRegex, replacement);
          }
        } else {
          // No matches -> remove the whole token + constraint cleanly
          logic = logic.replace(tokenRegex, "");
        }
      });

      // Clean up leftover whitespace/operators/empty parentheses
      logic = logic.replace(/\(\s*\)/g, ""); // empty parentheses
      logic = logic.replace(/\s+/g, " ").trim();
      // Remove stray operator sequences
      logic = logic.replace(/\b(and|or)\s+(and|or)\b/gi, "$1");
      logic = logic.replace(/^\s*(and|or)\s+/gi, "");
      logic = logic.replace(/\s+(and|or)\s*$/gi, "");
      // Remove "not" if dangling
      logic = logic.replace(/\bnot\s*$/gi, "");
      logic = logic.replace(/\bnot\s+(and|or)\b/gi, "$1");
      logic = logic.replace(/\b(and|or)\s+not\b/gi, "$1");
      // If parentheses wrap a single token, remove them
      logic = logic.replace(/\(\s*([^\(\)]+?)\s*\)/g, "$1");

      return logic;
    };

    // Get question data helper
    const getQuestionData = (qNum) => {
      if (Array.isArray(sdataGroup)) {
        return sdataGroup.find((q) => q.Question_number === qNum);
      }
      return (
        Object.values(sdataGroup).find((q) => q?.Question_number === qNum) ||
        sdataGroup[qNum]
      );
    };

    // MAIN
    const questionRefs = extractQuestionRefs(matchQuestionNumber);

    if (questionRefs.length === 0) {
      toast.warning("No valid question references found");
      return;
    }

    // Build questions map and track not-found
    const questionsMap = {};
    const notFound = [];

    questionRefs.forEach((qNum) => {
      const qData = getQuestionData(qNum);
      if (qData) {
        questionsMap[qNum] = qData;
      } else {
        notFound.push(qNum);
      }
    });

    if (notFound.length > 0) {
      toast.warning(`Questions not found: ${notFound.join(", ")}`);
      if (Object.keys(questionsMap).length === 0) return;
    }

    let matchCount = 0;

    const updatedRows = rows.map((currentRow) => {
      // For each questionRef, compute matches + capture constraintRaw + isGrid
      const questionsInfo = {};

      questionRefs.forEach((qNum) => {
        // capture constraints immediately following qNum in the original expression
        const m = matchQuestionNumber.match(new RegExp(`${qNum}([^\\s()]*)`));
        const constraintRaw = m ? m[1] : ""; // e.g. "=3,4" or ""
        const qData = questionsMap[qNum];

        const isGrid = !!(
          qData &&
          (qData.columns || qData.isGrid || qData.grid)
        );

        const matches =
          qData && qData.rows
            ? findMatchingRows(currentRow, qData, constraintRaw)
            : [];

        questionsInfo[qNum] = {
          matches,
          constraintRaw,
          isGrid,
        };
      });

      const logic = buildLogicWithRowNumbers(
        matchQuestionNumber,
        questionsInfo
      );

      // Only include row if there is something left in logic (i.e., at least one match)
      if (logic && logic.trim()) {
        matchCount++;
        return {
          ...currentRow,
          filterLogic: logic,
        };
      }

      // No matches -> remove filterLogic if present
      const { filterLogic, ...rest } = currentRow;
      return rest;
    });

    if (matchCount === 0) {
      toast.warning(`No matching rows found for: ${matchQuestionNumber}`);
      return;
    }

    // Update state (same as original)
    setRows(updatedRows);
    const updatedVariable = {
      ...selectedVariable,
      rows: updatedRows,
      ...(matchGrid === false && { piping: matchQuestionNumber }),
      ...(matchGrid === true && { remRow: true }),
    };

    if (matchGrid && selectedVariable?.Question_number) {
      setList(
        addRemRowToQnumInQuestionNumbers(selectedVariable.Question_number)
      );
    }
    setSelectedVariable(updatedVariable);
    setSdataGroup((prev) => ({ ...prev, [selectedTitle]: updatedVariable }));

    toast.success(`Found ${matchCount} matching rows for: ${matchQuestionNumber}`);
  };

  const addRemRowToQnumInQuestionNumbers = (qnum) => {
    return list.map((item) => {
      // Check if qnum exists in questionNumbers array
      const qnumIndex = item.questionNumbers.findIndex((q) => q.name === qnum);

      if (qnumIndex !== -1) {
        // Create a new questionNumbers array with remRow added to the matching qnum
        const updatedQuestionNumbers = [...item.questionNumbers];
        updatedQuestionNumbers[qnumIndex] = {
          ...updatedQuestionNumbers[qnumIndex],
          remRow: true,
        };

        return {
          ...item,
          questionNumbers: updatedQuestionNumbers,
        };
      }
      return item;
    });
  };

  const handleClearLogicRows = ({ mode = "all", indices = null } = {}) => {
    // defensive checks
    if (!selectedVariable) {
      toast.warning("No question selected.");
      return;
    }

    const isClearRows = mode === "all" || mode === "rows";
    const isClearCols = mode === "all" || mode === "cols";

    // Helper to check whether a given item matches an indices filter.
    // Items may have .index (explicit) or we use position fallback (pos is 0-based).
    const shouldClearIndex = (itemIndex, pos) => {
      if (!indices || indices.length === 0) return true; // clear all if no filter
      const idx = itemIndex ?? pos + 1;
      return indices.includes(idx);
    };

    // Work on a shallow copy of selectedVariable so we can update
    const updatedVariable = { ...selectedVariable };

    let clearedCount = 0;

    // Clear logic from rows
    if (isClearRows && Array.isArray(updatedVariable.rows)) {
      const newRows = updatedVariable.rows.map((r, idx) => {
        const rowIndex = r.index ?? idx + 1;
        if (!shouldClearIndex(rowIndex, idx)) return r;

        // Remove known logic properties if present.
        // Keep other properties intact.
        const { filterLogic, logic, _logic, ...rest } = r;

        // If rows might have per-cell logic (grid with cells), attempt to clear those too.
        // Common shapes: r.cells = [{ filterLogic: '...' }, ...] or r.columns, r.cols
        const newRow = { ...rest };

        if (r.cells && Array.isArray(r.cells)) {
          newRow.cells = r.cells.map((c) => {
            const { filterLogic: f1, logic: f2, _logic: f3, ...cRest } = c;
            if (f1 || f2 || f3) clearedCount++;
            return cRest;
          });
        } else if (r.columns && Array.isArray(r.columns)) {
          newRow.columns = r.columns.map((c) => {
            const { filterLogic: f1, logic: f2, _logic: f3, ...cRest } = c;
            if (f1 || f2 || f3) clearedCount++;
            return cRest;
          });
        }

        if (filterLogic || logic || _logic) clearedCount++;
        return newRow;
      });
      updatedVariable.piping = ""; // also clear any piping reference if exists
      updatedVariable.rows = newRows;
    }

    // Clear logic from columns (if your data uses a top-level columns array)
    if (isClearCols && Array.isArray(updatedVariable.columns)) {
      const newCols = updatedVariable.columns.map((c, idx) => {
        const colIndex = c.index ?? idx + 1;
        if (!shouldClearIndex(colIndex, idx)) return c;

        const { filterLogic, logic, _logic, ...rest } = c;
        // try clear nested cells if exist
        let countBefore = clearedCount;
        if (c.cells && Array.isArray(c.cells)) {
          rest.cells = c.cells.map((cell) => {
            const {
              filterLogic: f1,
              logic: f2,
              _logic: f3,
              ...cellRest
            } = cell;
            if (f1 || f2 || f3) clearedCount++;
            return cellRest;
          });
        }
        if (filterLogic || logic || _logic) clearedCount++;
        return rest;
      });

      updatedVariable.columns = newCols;
    }

    // Some datasets may store logic on selectedVariable.rows[*].filterLogic OR rows[*].filter or rows[*].meta.filterLogic.
    // The code above handles direct top-level keys. If you use a different shape, add similar destructuring above.

    // Update state: replace selectedVariable, rows, and sdataGroup accordingly
    try {
      // If your UI also uses a 'rows' state variable, update it:
      if (typeof setRows === "function") {
        // If the tool's rows variable is directly selectedVariable.rows, update that.
        // Otherwise fallback: if a top-level 'rows' var exists in this scope, we update it.
        if (Array.isArray(updatedVariable.rows)) {
          setRows(updatedVariable.rows);
        } else {
          // if rows variable exists in outer scope, clear filterLogic there as well
          try {
            setRows((prevRows) =>
              (prevRows || []).map((r, idx) => {
                const rowIndex = r.index ?? idx + 1;
                if (!isClearRows || (indices && !indices.includes(rowIndex)))
                  return r;
                const { filterLogic, logic, _logic, ...rest } = r;
                if (r.cells && Array.isArray(r.cells)) {
                  rest.cells = r.cells.map((c) => {
                    const {
                      filterLogic: f1,
                      logic: f2,
                      _logic: f3,
                      ...cRest
                    } = c;
                    return cRest;
                  });
                }
                return rest;
              })
            );
          } catch (e) {
            // ignore if setRows signature is different
          }
        }
      }

      // Update selected variable state used in the tool
      if (typeof setSelectedVariable === "function") {
        setSelectedVariable(updatedVariable);
      }

      // Update global sdataGroup if available
      if (typeof setSdataGroup === "function" && selectedTitle) {
        setSdataGroup((prev) => ({
          ...prev,
          [selectedTitle]: updatedVariable,
        }));
      }

      toast.success(`Cleared logic from ${clearedCount > 0 ? clearedCount : "0"} place(s).`);
    } catch (err) {
      toast.error("Something went wrong while clearing logic.");
    }
  };

  // === Validation / Normalization utility (drop into SurveyBuilder) ===
  const defaultOptions = {
    maxKeyLength: 30, // shorten keys longer than this
    keySeparator: "x", // replace spaces with this
    dedupSuffixPrefix: "x", // suffix prefix for duplicates (e.g. x1)
    shiftColumnsToRows: true, // behavior requested
    // validators: array of (question, key, fullData) => { question, key } // optional extend
  };

  const safeKey = (raw, opts = {}) => {
    const { keySeparator } = { ...defaultOptions, ...opts };
    if (!raw) return "q";
    // convert to string, lowercase for stability
    let k = String(raw).trim();

    // replace spaces with separator and remove unsafe chars except _ and alnum
    k = k.replace(/\s+/g, keySeparator);
    k = k.replace(/[^A-Za-z0-9_\-x]/g, ""); // allow letters, digits, underscore, dash, and our 'x'

    // ensure it doesn't start with a digit (prefix with 'q' if so)
    if (/^\d/.test(k)) k = `q${k}`;

    // if after cleaning it's empty, fallback
    if (!k) k = "q";
    return k;
  };

  const validateSurveyData = (data, options = {}) => {
    const opts = { ...defaultOptions, ...options };
    const validators = opts.validators || [];

    // Work with object mapping: if data is array, convert to map using Question_number or index
    const isArray = Array.isArray(data);
    const entries = isArray
      ? data.map((q, i) => [q?.Question_number ?? `q${i + 1}`, q])
      : Object.entries(data || {});

    const newData = {};
    const usedKeys = {}; // track duplicates map -> count

    const makeUnique = (base) => {
      if (!usedKeys[base]) {
        usedKeys[base] = 1;
        return base;
      }
      // if base already used, append dedupSuffixPrefix + count
      usedKeys[base] += 1;
      return `${base}${opts.dedupSuffixPrefix}${usedKeys[base] - 1}`;
    };

    entries.forEach(([origKey, question]) => {
      // prefer existing Question_number or Qtitle if key blank
      const fallback =
        (question && (question.Question_number || question.Qtitle)) ||
        origKey ||
        "q";

      // 1) produce safe base key
      let baseKey = safeKey(fallback, opts);

      // 2) shorten if too long but keep uniqueness by keeping end part as well
      if (opts.maxKeyLength && baseKey.length > opts.maxKeyLength) {
        // keep first 18 and last (maxKeyLength - 18 - 1) to preserve uniqueness
        const keepStart = Math.max(10, Math.floor(opts.maxKeyLength / 2));
        const keepEnd = Math.max(6, opts.maxKeyLength - keepStart - 1);
        baseKey = `${baseKey.slice(0, keepStart)}${opts.keySeparator
          }${baseKey.slice(-keepEnd)}`;
        // clean again
        baseKey = baseKey.replace(/[^A-Za-z0-9_]/g, "");
      }

      // 3) ensure uniqueness by appending x1, x2... via makeUnique
      let finalKey = makeUnique(baseKey);

      // 4) run custom validators (allow them to modify question or key)
      let qClone = { ...(question || {}) };
      validators.forEach((v) => {
        try {
          const out = v(qClone, finalKey, data);
          if (out) {
            if (out.key) finalKey = safeKey(out.key, opts);
            if (out.question) qClone = out.question;
          }
        } catch (e) {
          // ignore individual validator errors — keep processing
          console.error("Validator error:", e);
        }
      });

      // 5) SHIFT columns -> rows if requested & rows blank
      if (opts.shiftColumnsToRows) {
        const rowsEmpty =
          !qClone.rows ||
          (Array.isArray(qClone.rows) &&
            qClone.rows.every((r) => {
              const t = r?.text ?? r?.label ?? "";
              return !String(t).trim();
            }));

        const columnsHasValues =
          Array.isArray(qClone.columns) &&
          qClone.columns.some((c) => {
            const t = c?.text ?? c?.label ?? "";
            return String(t).trim();
          });

        if (rowsEmpty && columnsHasValues) {
          // create rows from columns
          const newRows = (qClone.columns || []).map((c, i) => {
            const text = c?.text ?? c?.label ?? "";
            // keep other useful flags if present
            return {
              index: `r${i + 1}`,
              text: String(text).trim(),
              other: c.other || false,
              exclusive: c.exclusive || false,
              screenout: c.screenout || false,
              // copy any additional safe props you want
            };
          });
          qClone.rows = newRows;
          qClone.columns = []; // clear columns (you can change behavior if you want to keep them)
        }
      }

      // 6) normalize row/column indices: ensure no spaces, use r1/c1, fill missing index
      if (Array.isArray(qClone.rows)) {
        qClone.rows = qClone.rows.map((r, idx) => {
          const currentIndex = r?.index ?? r?.id ?? `r${idx + 1}`;
          return {
            ...r,
            index:
              String(currentIndex)
                .toString()
                .replace(/\s+/g, "")
                .replace(/[^A-Za-z0-9_]/g, "") || `r${idx + 1}`,
          };
        });
      }
      if (Array.isArray(qClone.columns)) {
        qClone.columns = qClone.columns.map((c, idx) => {
          const currentIndex = c?.index ?? c?.id ?? `c${idx + 1}`;
          return {
            ...c,
            index:
              String(currentIndex)
                .toString()
                .replace(/\s+/g, "")
                .replace(/[^A-Za-z0-9_]/g, "") || `c${idx + 1}`,
          };
        });
      }

      // 7) Finally assign into newData using finalKey
      newData[finalKey] = qClone;
    });

    return newData;
  };

  const generateListId = (questionNumber, type) => {
    // Clean the question number to make it a valid ID
    const cleanQuestionNumber = questionNumber.replace(/[^a-zA-Z0-9_]/g, "_");
    const typeSuffix =
      type === "rows"
        ? "_rows"
        : type === "columns"
          ? "_columns"
          : "_statements";

    return `${cleanQuestionNumber}${typeSuffix}`;
  };

  const {
    addAllLists,
    isProcessing,
    lastResult,
    error,
    stats,
    getSummary,
    reset,
  } = useListProcessor({
    showAlerts: true,
    onSuccess: (result) => {
      console.log("Processing succeeded:", result.summary);
    },
    onError: (err) => {
      console.error("Processing failed:", err);
    },
  });

  const handleListUpdate = (updatedList) => {
    setList((prevLists) => {
      const listKey = Object.keys(updatedList)[0];
      return prevLists.map((list) => {
        const currentKey = Object.keys(list)[0];
        if (currentKey === listKey || list.id === updatedList.id) {
          return updatedList;
        }
        return list;
      });
    });
  };

  const handleAddAllLists = async () => {
    try {
      const result = await addAllLists(sdataGroup, list, setList);

      // Open modal after successful processing
      setIsAllListsModalOpen(true);

      return result;
    } catch (err) {
      // Error is already handled by the hook
      console.error("Failed to process lists:", err);
    }
  };
  const organization = user?.user?.organization || user?.organization;
  const hasValidOrganization = organization?.id ? true : false;

  const features = user?.user?.organization.scripting_feature || user?.organization.scripting_feature || {};
  // === End of validation utility ===

  return (
    <div className="survey-builder">
      {/* {JSON.stringify(sdataGroup)} */}
      {/* {JSON.stringify(selectedVariable)} */}
      {/* <CollaborationBar
        isConnected={isConnected}
        activeUsers={activeUsers}
        questionLocks={questionLocks}
        selectedQuestion={selectedTitle}
        currentUserId={currentUserEmail} // Using email as ID
        connectionError={connectionError}
      /> */}

      {/* Debug Info (remove in production) */}
      {/* {process.env.NODE_ENV === "development" && (
        <div
          style={{
            position: "fixed",
            bottom: "10px",
            left: "10px",
            background: "var(--color-border)",
            padding: "10px",
            borderRadius: "4px",
            fontSize: "11px",
            zIndex: 9999,
          }}
        >
          <div>
            <strong>Debug Info:</strong>
          </div>
          <div>Email: {currentUserEmail}</div>
          <div>Username: {currentUsername}</div>
          <div>Connected: {isConnected ? "✅" : "❌"}</div>
          <div>Active Users: {activeUsers.length}</div>
          <div>Project: {projectId}</div>
        </div>
      )} */}

      {/* 🔥 ADD NOTIFICATION CONTAINER */}
      {/* <div
        style={{
          position: "fixed",
          top: "20px",
          right: "20px",
          zIndex: 10000,
          display: "flex",
          flexDirection: "column",
          gap: "10px",
        }}
      >
        {notifications.map((notif) => (
          <div
            key={notif.id}
            style={{
              background:
                notif.type === "warning"
                  ? "#ff9800"
                  : notif.type === "info"
                  ? "#2196f3"
                  : "#4caf50",
              color: "white",
              padding: "12px 20px",
              borderRadius: "6px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              fontSize: "14px",
              maxWidth: "350px",
              animation: "slideIn 0.3s ease",
            }}
          >
            {notif.message}
          </div>
        ))}
      </div> */}
      <div style={{ width: "100%" }}>
        {/* TOP TOOLBAR */}
        <div className="survey-builder-toolbar">
          {/* LEFT SIDE: Action Buttons */}
          <div className="toolbar-actions">
            {/* <h3 style={{ margin: 0, fontSize: "24px" }}>Survey Builder</h3> */}
            {features.global_settings && (
              <CommonButton
                label=""
                symbol={faCog}
                title="Global Settings"
                onClick={() => setShowGlobalSettingsDropdown(!showGlobalSettingsDropdown)}
              />
            )}
            {features.quota_settings && (
              <CommonButton
                label="Quota"
                title="Open Quota Settings"
                onClick={handleOpenQuotaModal}
              />
            )}
            {features.redirects_settings && (
              <CommonButton
                label="Redirects"
                title="Manage Redirect URLs"
                onClick={handleOpenRedirectModal}
              />
            )}

            {showQuotaModal && (
              <QuotaModal
                isOpen={showQuotaModal}
                onClose={handleCloseQuotaModal}
                onSave={handleSaveQuota}
                initialData={quotaData}
                sdataGroup={sdataGroup}
                projectId={projectId}
              />
            )}
            {showRedirectModal && (
              <RedirectModal
                isOpen={showRedirectModal}
                onClose={() => setShowRedirectModal(false)}
                onSave={handleSaveRedirect}
                initialData={redirectData}
                projectId={projectId}
              />
            )}
            {features.all_lists && (
              <CommonButton
                label="All Lists"
                symbol={faListAlt}
                onClick={() => {
                  handleAddAllLists();
                  setTimeout(() => {
                    setIsAllListsModalOpen(true);
                  }, 100);
                }}
                title="Create lists and view all lists"
              />
            )}

            {features.actions && (<CommonButton
              label="Actions"
              symbol={faFileCircleCheck}
              onClick={() => setBulkActionsModalOpen(true)}
              title="Select, move, or delete multiple questions"
            />
            )}
            {features.history && (<CommonButton
              label="History"
              symbol={faClockRotateLeft}
              onClick={() => {
                setChangeLogFilterKey(null);
                setChangeLogModalOpen(true);
              }}
              title="View the change log and revert changes"
            />
            )}
            <ChangeLogModal
              isOpen={changeLogModalOpen}
              onClose={() => setChangeLogModalOpen(false)}
              entries={changeLogEntries}
              onRevert={handleRevertChange}
              filterQuestionKey={changeLogFilterKey}
              questionLabel={
                changeLogFilterKey
                  ? sdataGroup?.[changeLogFilterKey]?.Question_number ||
                  changeLogFilterKey
                  : ""
              }
            />

            <AllListsModal
              isOpen={isAllListsModalOpen}
              onClose={() => setIsAllListsModalOpen(false)}
              lists={list}
              sdataGroup={sdataGroup}
              onListUpdate={handleListUpdate}
            />

            <BulkActionsModal
              isOpen={bulkActionsModalOpen}
              onClose={() => setBulkActionsModalOpen(false)}
              sdataGroup={sdataGroup}
              selectedQuestionsForBulk={selectedQuestionsForBulk}
              setSelectedQuestionsForBulk={setSelectedQuestionsForBulk}
              getSortedQuestionKeys={getSortedQuestionKeys}
              moveSelectedQuestions={moveSelectedQuestions}
              handleBulkDelete={handleBulkDelete}
            />
            {/* {features.quick_preview && (
              <QuestionPreview
                label="Quick Preview"
                surveyData={sdataGroup}
                question={selectedVariable}
                questionNumber={selectedTitle}
                questionnaireFileId={questionnaireFileId}
                surveyFileName={surveyFileName}
              />
            )} */}
            {features.preview && (
              <SurveyPreview
                label="Preview"
                pid={pID}
                pname={pName}
                sdataGroup={sdataGroup}
                surveyName={surveyName}
                surveyFileName={surveyFileName}
                oeParameters={oeParameters}
                onShowToastMessage={(message) => setToastMessage(message)}
                onSetProcessingMessage={(message) => setProcessingMessage(message)}
                questionnaireFileId={questionnaireFileId}
                exportedFiles={exportedFiles}
                powner={powner}
                projectId={projectId}
                selectedClient={selectedClient}
                selectedProject={selectedProject}
                onSurveyUpdate={(updated) => setSdataGroup(updated)}
              />
            )}
            <CommonButton
              symbol={faRotateLeft}
              variant="secondary"
              title="Undo (Ctrl+Z)"
              disabled={!canUndo}
              onClick={() => undo()}
            />
            <CommonButton
              symbol={faRotateRight}
              variant="secondary"
              title="Redo (Ctrl+Y)"
              disabled={!canRedo}
              onClick={() => redo()}
            />
            {features.save_survey && (
              <CommonButton
                symbol={faSave}
                variant="success"
                title="Save Survey"
                onClick={() => handleSaveSurvey()}
              />
            )}
            {features.add_to_tables && (
              <CommonButton
                symbol={faTable}
                variant="purple"
                title="Add to Tables"
                onClick={() => handleAddTable()}
              />
            )}
          </div>

          {/* RIGHT SIDE: Export/Download Buttons */}
          <ExportOptionsDropdown
            features={features}
            projectId={projectId}
            sdataGroup={sdataGroup}
            setSdataGroup={setSdataGroup}
            banners={banners}
            selectedProject={selectedProject}
            selectedClient={selectedClient}
            surveyName={surveyName}
            surveyFileName={surveyFileName}
            user={user}
            powner={powner}
            list={list}
            columnIndex={columnIndex}
            autoPreQuotes={autoPreQuotes}
            alphaOrder={alphaOrder}
            hideme={hideme}
            qtitlehide={qtitlehide}
            removebold={removebold}
            removinstt={removinstt}
            quotaData={quotaData}
            templateOptions={templateOptions}
            selectedTemplateValue={selectedTemplateValue}
            onExportFilesUpdate={(exportType, exportData) =>
              setExportedFiles((prev) => ({ ...prev, [exportType]: exportData }))
            }
          />
        </div>
        {/* ⚠️ CRITICAL: QUESTION HEADER SECTION - WAS MISSING! */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "10px",
            flexWrap: "wrap",
            gap: "16px",
          }}
        >
          {/* Left side: Question number, type label, single exports */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap",
              flex: "1 1 300px",
            }}
          >
            <input
              type="text"
              value={
                selectedVariable?.["Question_number"] || selectedTitle || ""
              }
              onChange={(e) => handleTitleChange("Question_number", e)}
              onFocus={() => {
                questionNumberFocusValueRef.current =
                  selectedVariable?.Question_number || selectedTitle || "";
              }}
              onBlur={handleQuestionNumberBlur}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.target.blur(); // commit rename on Enter
              }}
              placeholder="Question Number"
              style={{
                padding: "8px 12px",
                fontSize: "14px",
                fontWeight: "500",
                border: "1px solid #ccc",
                borderRadius: "6px",
                minWidth: "100px",
              }}
            />

            <span
              style={{ fontSize: "16px", fontWeight: "500", color: "#444" }}
            >
              - {selectedVariable?.type || "Single Response"}
            </span>

            {selectedTitle && (
              <div style={{ display: "flex", gap: "6px" }}>
                {hasDownloadAccess("decipher_xml") && (
                  <CommonButton
                    label="DC"
                    onClick={() => exportSingleQuestion("DC")}
                  />
                )}
                {hasDownloadAccess("dimension") && (
                  <CommonButton
                    label="DM"
                    onClick={() => exportSingleQuestion("DM")}
                  />
                )}
                {hasDownloadAccess("qualtrics") && (
                  <CommonButton
                    label="QT"
                    onClick={() => exportSingleQuestion("QT")}
                  />
                )}
              </div>
            )}
          </div>

          {/* Right side: Settings, Type, Delete */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              flexShrink: 0,
            }}
          >
            <CommonButton
              label=""
              symbol={faCog}
              title="Settings"
              onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
            />

            {selectedTitle && (
              <CommonButton
                label=""
                symbol={faClockRotateLeft}
                title="View change history for this question"
                onClick={() => {
                  setChangeLogFilterKey(selectedTitle);
                  setChangeLogModalOpen(true);
                }}
              />
            )}

            {selectedTitle && (
              <CommonButton
                label=""
                symbol={faTrashAlt}
                title="Delete Question"
                onClick={() => deleteQuestion()}
                variant="danger"
              />
            )}
            <HoverDropdown
              handleTitleChange={handleTitleChange}
              text="Type"
              exportfeatures={questionType}
              feature={5}
            />
          </div>
        </div>

        {/* SETTINGS DROPDOWN */}
        {showSettingsDropdown && (
          <div
            className="settings-dropdown-container sb-settings-panel"
            style={{ position: "absolute", right: "20px", top: "140px", zIndex: 1000 }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="sb-settings-header">
              <div className="sb-settings-header-left">
                <FontAwesomeIcon icon={faCog} style={{ marginRight: "8px" }} />
                Question Settings
              </div>
              <div className="sb-settings-header-right">
                <button className="sb-settings-close" onClick={() => setShowSettingsDropdown(false)} title="Close">
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            </div>
            <div className="sb-settings-body">
              <label
                style={{
                  fontSize: "12px",
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={autoPreQuotes}
                  onChange={(e) => setAutoPreQuotes(e.target.checked)}
                  style={{ marginRight: "8px" }}
                />
                PreQuotes
              </label>

              <label
                style={{
                  fontSize: "12px",
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedVariable?.columnIndex || false}
                  onChange={(e) =>
                    handleCheckboxChange("columnIndex", e.target.checked)
                  }
                  style={{ marginRight: "8px" }}
                />
                Column Index
              </label>
              <label
                style={{
                  fontSize: "12px",
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedVariable?.alphabeticalOrder || false}
                  onChange={(e) =>
                    handleCheckboxChange("alphabeticalOrder", e.target.checked)
                  }
                  style={{ marginRight: "8px" }}
                />
                Alphabetical Order (A–Z)
              </label>
              {selectedTitle && (
                <>
                  <div
                    style={{
                      borderTop: "1px solid #e5e7eb",
                      margin: "4px 0",
                    }}
                  ></div>
                  <label
                    style={{
                      fontSize: "12px",
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={
                        activeTab === "rows"
                          ? selectedVariable?.rowsRandomisation || false
                          : selectedVariable?.columnsRandomisation || false
                      }
                      onChange={(e) =>
                        handleCheckboxChange(
                          activeTab === "rows"
                            ? "rowsRandomisation"
                            : "columnsRandomisation",
                          e.target.checked
                        )
                      }
                      style={{ marginRight: "8px" }}
                    />
                    {activeTab === "rows" ? "Rows" : "Columns"} Randomisation
                  </label>
                  <label
                    style={{
                      fontSize: "12px",
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={
                        activeTab === "rows"
                          ? selectedVariable?.rowsRotation || false
                          : selectedVariable?.columnsRotation || false
                      }
                      onChange={(e) =>
                        handleCheckboxChange(
                          activeTab === "rows"
                            ? "rowsRotation"
                            : "columnsRotation",
                          e.target.checked
                        )
                      }
                      style={{ marginRight: "8px" }}
                    />
                    {activeTab === "rows" ? "Rows" : "Columns"} Rotation
                  </label>
                  <label
                    style={{
                      fontSize: "12px",
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={
                        activeTab === "rows"
                          ? selectedVariable?.rowFlipped || false
                          : selectedVariable?.columnsFlipped || false
                      }
                      onChange={(e) =>
                        handleCheckboxChange(
                          activeTab === "rows"
                            ? "rowFlipped"
                            : "columnsFlipped",
                          e.target.checked
                        )
                      }
                      style={{ marginRight: "8px" }}
                    />
                    {activeTab === "rows" ? "Rows" : "Columns"} Flipped
                  </label>
                  <label
                    style={{
                      fontSize: "12px",
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={
                        activeTab === "rows"
                          ? selectedVariable?.rowAlphaOrder || false
                          : selectedVariable?.columnsAlphaOrder || false
                      }
                      onChange={(e) =>
                        handleCheckboxChange(
                          activeTab === "rows"
                            ? "rowAlphaOrder"
                            : "columnsAlphaOrder",
                          e.target.checked
                        )
                      }
                      style={{ marginRight: "8px" }}
                    />
                    {activeTab === "rows" ? "Rows" : "Columns"} Alpha Order
                  </label>
                </>
              )}
            </div>
          </div>
        )}

      </div>
      {/* FULL-WIDTH INPUT FIELDS */}
      {selectedTitle && (
        <div className="question-meta-grid">
          <FormattableInput
            value={selectedVariable?.Qtitle || ""}
            onChange={(e) => handleTitleChange("Qtitle", e)}
            placeholder="Question Title / Confidentiality Agreement"
            className="question-title-input"
            type="contentEditable"
          />

          <FormattableInput
            value={selectedVariable?.instructions || ""}
            onChange={(e) => handleTitleChange("instructions", e)}
            placeholder="Instructions (e.g. Please select:)"
            className="question-instruction-input"
            type="contentEditable"
          />

          <FormattableInput
            value={selectedVariable?.logics || ""}
            onChange={(e) => handleTitleChange("logics", e)}
            placeholder="Logic [e.g. S5=1-3,6; grid e.g. S6 r3-4,6=1,2; pipe e.g. pipe S4; pipe S6=2-4 etc.]"
            className="question-instruction-input"
            type="contentEditable"
          />

          <FormattableInput
            value={selectedVariable?.termLogic || ""}
            onChange={(e) => handleTitleChange("termLogic", e)}
            placeholder="Termination Logic"
            className="question-instruction-input"
            type="contentEditable"
          />
        </div>
      )}

      {/* Tabs */}
      {selectedTitle && (
        <>
          {
            <div
              className="tabs"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "10px",
                // marginBottom: "15px",
              }}
            >
              {/* Left side - tabs with switch arrows between them */}
              <div
                style={{ display: "flex", alignItems: "center", gap: "4px" }}
              >
                {/* Rows Tab */}
                <CommonButton
                  label="Rows"
                  title="Rows"
                  onClick={() => setActiveTab("rows")}
                  variant="tab"
                  isSelected={activeTab === "rows"}
                />

                <CommonButton
                  label="↔"
                  title="Switch Rows ↔ Columns"
                  onClick={() => handleSwitchBetween("rows", "columns")}
                  variant="arrow"
                />
                <CommonButton
                  label="Columns"
                  title="Columns"
                  onClick={() => setActiveTab("columns")}
                  variant="tab"
                  isSelected={activeTab === "columns"}
                />
                <CommonButton
                  label="↔"
                  title="Switch Columns ↔ Statements"
                  onClick={() => handleSwitchBetween("columns", "statements")}
                  variant="arrow"
                />
                <CommonButton
                  label="Statements"
                  title="Statements"
                  onClick={() => setActiveTab("statements")}
                  variant="tab"
                  isSelected={activeTab === "statements"}
                />
                <CommonButton
                  label="⤴"
                  title="Switch Statements ↔ Rows"
                  onClick={() => handleSwitchBetween("statements", "rows")}
                  variant="arrow"
                />
                <CommonButton
                  label="Parameters"
                  title="Parameters"
                  onClick={() => setActiveTab("oeParameters")}
                  variant="tab"
                  isSelected={activeTab === "oeParameters"}
                  style={{ marginLeft: "6px" }}
                />
              </div>

              {/* Row Matching Section */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  marginLeft: "12px",
                }}
              >
                <label
                  style={{ fontSize: "12px", fontWeight: "500", color: "var(--color-gray-500, #6b7280)" }}
                >
                  Piping:
                </label>
                <input
                  type="text"
                  placeholder="Add Piping"
                  value={selectedVariable?.piping}
                  onChange={(e) => {
                    setMatchQuestionNumber(e.target.value);
                    setSelectedVariable({
                      ...selectedVariable,
                      piping: e.target.value,
                    });
                  }}
                  style={{
                    padding: "4px 8px",
                    fontSize: "12px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    backgroundColor: "white",
                    width: "80px",
                  }}
                  title="Enter question number to match rows from"
                />

                {/* Match Button */}
                <CommonButton
                  label="Match"
                  title="Match rows from the specified question"
                  onClick={handleMatchRows}
                  variant="smallAction"
                />
                <CommonButton
                  label="Clear"
                  title="It will clear existing logic"
                  onClick={handleClearLogicRows}
                  variant="smallAction"
                />
              </div>

              {/* Conditional Rows/Columns */}

              {activeTab === "rows" && (
                <RowColumnComponent
                  items={rows}
                  addadditionalRow={addadditionalRow}
                  deleteRow={deleteRow}
                  label="Rows"
                  savePropertyChanges={savePropertyChanges}
                  handlePaste={(e) => handlePaste(e, "rows")}
                  addList={addList}
                  qType={selectedVariable.type || "SingleResponse"}
                  list={list}
                  setList={setList}
                  selectedVariable={selectedVariable}
                  screenOutSelections={screenOutSelections}
                  onScreenOutChange={(index, checked) =>
                    handleScreenOutChange(index, checked, "rows")
                  }
                />
              )}
              {activeTab === "columns" && (
                <RowColumnComponent
                  items={columns}
                  addadditionalRow={addadditionalRow}
                  label="Columns"
                  deleteRow={deleteCol}
                  qType={selectedVariable.type || "SingleResponse"}
                  savePropertyChanges={savePropertyChanges}
                  handlePaste={(e) => handlePaste(e, "columns")}
                  addList={addList}
                  list={list}
                  setList={setList}
                  selectedVariable={selectedVariable}
                  screenOutSelections={screenOutSelections}
                  onScreenOutChange={(index, checked) =>
                    handleScreenOutChange(index, checked, "columns")
                  }
                />
              )}
              {activeTab === "statements" && (
                <RowColumnComponent
                  items={statements}
                  addadditionalRow={addadditionalRow}
                  label="Statements"
                  deleteRow={deleteStatements}
                  qType={selectedVariable.type || "SingleResponse"}
                  savePropertyChanges={savePropertyChanges}
                  handlePaste={(e) => handlePaste(e, "statements")}
                  addList={addList}
                  setList={setList}
                  selectedVariable={selectedVariable}
                  list={list}
                  screenOutSelections={screenOutSelections}
                  onScreenOutChange={(index, checked) =>
                    handleScreenOutChange(index, checked, "statements")
                  }
                />
              )}
              {activeTab === "oeParameters" && (
                <OESettingsComponent
                  oeParameters={oeParameters || {}}
                  qtype={selectedVariable?.type}
                  selectedVariable={selectedVariable}
                  onQuotaUpsert={handleQuotaUpsert}
                  onQuotaRemove={handleQuotaRemove}
                  existingQuotaQuestion={Object.values(sdataGroup).find(q => q.Question_number === `${selectedVariable?.Question_number}_HIDDEN_QUOTA`)}
                  addadditionalRow={addadditionalRow}
                  handleOeChange={handleOeChange}
                  setOeParameters={setOeParameters}
                  label="Open end Parameters"
                  deleteRow={deleteCol}
                  savePropertyChanges={savePropertyChanges}
                  handlePaste={(e) => handlePaste(e, "oe")}
                  addList={addList}
                  list={list}
                  screenOutSelections={screenOutSelections}
                  availableQuotas={quotaData?.quotas || []}
                  onAssignQuota={(quotaName) => {
                    const updated = { ...selectedVariable };
                    if (quotaName) {
                      updated.assignedQuota = quotaName;
                    } else {
                      delete updated.assignedQuota;
                    }
                    setSelectedVariable(updated);
                    setSdataGroup((prev) => ({ ...prev, [selectedTitle]: updated }));
                  }}
                  onOpenQuotaModal={handleOpenQuotaModal}
                />
              )}
              {pasteModalOpen && (
                <></>
              )}
            </div>
          }
        </>
      )}
      {/* Code Dialog Modal */}
      <AddToTableModal
        isOpen={toolSelectionModalOpen}
        onClose={() => setToolSelectionModalOpen(false)}
        projectId={projectId}
        sdataGroup={sdataGroup}
        onSuccess={handleAddTableSuccess}
      />

      {codeDialogOpen && (
        <div
          style={{
            position: "fixed",
            top: "0",
            left: "0",
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
          onClick={() => setCodeDialogOpen(false)}
        >
          <div
            style={{
              background: "#fff",
              padding: "20px",
              borderRadius: "12px",
              width: "80%",
              maxWidth: "900px",
              maxHeight: "80vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "15px",
                borderBottom: "2px solid var(--color-border)",
                paddingBottom: "10px",
              }}
            >
              <h3 style={{ margin: 0, color: "var(--color-gray-800, #1f2937)" }}>{codeDialogTitle}</h3>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={copyCodeToClipboard}
                  style={{
                    background: "var(--color-success)",
                    color: "var(--color-white)",
                    border: "none",
                    borderRadius: "var(--radius-xl)",
                    padding: "8px 18px",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: "600",
                    transition: "all 0.2s ease",
                    boxShadow: "var(--shadow-xs)",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "var(--color-emerald-600)";
                    e.target.style.transform = "translateY(-1px)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "var(--color-success)";
                    e.target.style.transform = "none";
                  }}
                >
                  Copy Code
                </button>
                <button
                  onClick={() => setCodeDialogOpen(false)}
                  style={{
                    background: "var(--color-gray-100)",
                    color: "var(--color-gray-700)",
                    border: "none",
                    borderRadius: "var(--radius-xl)",
                    padding: "8px 18px",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: "600",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "var(--color-gray-200)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "var(--color-gray-100)";
                  }}
                >
                  Close
                </button>
              </div>
            </div>
            <div
              style={{
                flex: 1,
                overflow: "auto",
                background: "var(--color-bg-subtle, #f8fafc)",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                padding: "15px",
              }}
            >
              <pre
                style={{
                  margin: 0,
                  fontFamily: "monospace",
                  fontSize: "13px",
                  lineHeight: "1.5",
                  whiteSpace: "pre-wrap",
                  wordWrap: "break-word",
                  color: "var(--color-gray-900, #1f2937)",
                }}
              >
                {generatedCode}
              </pre>
            </div>
          </div>
        </div>
      )}
      {/* <VoiceAssistant
          surveyData={sdataGroup}
          onSurveyUpdate={setSdataGroup}
          selectedTitle={selectedTitle}
          setSelectedVariable={setSelectedVariable}
          onNavigateToQuestion={handleNavigateToQuestion}
        /> */}

      {/* <pre>{JSON.stringify(sdataGroup, null, 2)}</pre> */}
      {showGlobalSettingsDropdown && (
        <div
          className="settings-dropdown-container sb-settings-panel"
          style={{ position: "absolute", left: "520px", top: "140px", zIndex: 1000 }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="sb-settings-header">
            <div className="sb-settings-header-left">
              <FontAwesomeIcon icon={faCog} style={{ marginRight: "8px" }} />
              Global Settings
            </div>
            <div className="sb-settings-header-right">
              <button className="sb-settings-close" onClick={() => setShowGlobalSettingsDropdown(false)} title="Close">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>
          <div className="sb-settings-body">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginLeft: "12px",
              }}
            >
              <label
                style={{
                  fontSize: "12px",
                  fontWeight: "500",
                  color: "var(--color-gray-500, #6b7280)",
                }}
              >
                Template:
              </label>
              <select
                value={selectedTemplateValue}
                onChange={(e) => setSelectedTemplateValue(e.target.value)}
                style={{
                  padding: "4px 8px",
                  fontSize: "12px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  backgroundColor: "white",
                  minWidth: "120px",
                }}
              >
                {templateOptions.map((template) => (
                  <option key={template.value} value={template.value}>
                    {template.label}
                  </option>
                ))}
              </select>
            </div>



            <label
              style={{
                fontSize: "12px",
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={removeNumber}
                onChange={(e) => setRemoveNumber(e.target.checked)}
                style={{ marginRight: "8px" }}
              />
              removeNumber
            </label>
            <label
              style={{
                fontSize: "12px",
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={hideme}
                onChange={(e) => setHideme(e.target.checked)}
                style={{ marginRight: "8px" }}
              />
              use hideme for hidden variables
            </label>

            <label
              style={{
                fontSize: "12px",
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={qtitlehide}
                onChange={(e) => setQtitlehide(e.target.checked)}
                style={{ marginRight: "8px" }}
              />
              Remove Question number from title
            </label>
            <label
              style={{
                fontSize: "12px",
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={removebold}
                onChange={(e) => setRemovebold(e.target.checked)}
                style={{ marginRight: "8px" }}
              />
              Questionnaire has all titles bold remove the bold formatting from titles
            </label>
            <label
              style={{
                fontSize: "12px",
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={removinstt}
                onChange={(e) => setRemovinstt(e.target.checked)}
                style={{ marginRight: "8px" }}
              />
              Remove the default instructions text like "Please select" from questions
            </label>

          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyBuilder;